import {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useHistory } from "react-router-dom";
import * as FileSaver from "file-saver";
import writeExcelFile from "write-excel-file/browser";
import "./auto-process.css";
import Api from "../../../service/api";
import UserContext from "../../../context/user";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Card,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  IcoCheckCircle,
  IcoAlertCircle,
  IcoBuilding2,
  IcoUser,
  IcoX,
  IcoCheckCircle2,
  IcoChevLeft,
  IcoChevRight,
  IcoChevDown,
  IcoDownload,
  IcoSquare,
  IcoSquareCheck,
  IcoSquareMinus,
  IcoSearch,
  IcoSend,
  IcoTriangle,
} from "./icons";
import SidePanel from "./SidePanel";
import ClearReviewModal from "./ClearReviewModal";
import SubmitReviewModal from "./SubmitReviewModal";
import SkipReviewModal from "./SkipReviewModal";
import UserGuideModal from "./UserGuideModal";
import ResultModal, { StatusIcon, StatusLabel } from "./ResultModal";
import AutoProcessFilters from "./AutoProcessFilters";
import {
  STATUS_CONDITION_FIELDS,
  PAGE_SIZE,
  REVIEW_BULK_FETCH_PAGE_SIZE,
  REVIEW_BULK_FETCH_BATCH_PAGES,
  REVIEW_BULK_FETCH_BATCH_THRESHOLD,
  REVIEW_STATUS_API_POLL_INTERVAL_MS,
  REVIEW_MAX_POLLING_TIME_MS,
  REVIEW_RETRY_WAIT_TIME_MS,
  REVIEW_MAX_RETRIES,
  REVIEW_SOURCE_ALIASES,
  REVIEW_RESULT_FIELD_CONFIG,
  AUTO_PREVIEW_FIELD_COUNT,
  AUTO_ENTITY_FIELD_CONFIG,
  AUTO_PROGRESS_POLL_INTERVAL_MS,
  AUTO_PROGRESS_POLL_WINDOW_MS,
  AUTO_MANUAL_REFRESH_WINDOW_MS,
  STATIC_AUTO_RESULT_DETAIL,
} from "../../../Constant/AutoProcessConstants";
export { STATUS_CONDITION_FIELDS };

const getReviewDeadline = (createdAt) => {
  if (!createdAt) return null;
  const match = String(createdAt).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/,
  );
  const createdDate = match
    ? new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6]),
        Number((match[7] || "").padEnd(3, "0").slice(0, 3)),
      )
    : new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) return null;
  createdDate.setDate(createdDate.getDate() + 5);
  return createdDate;
};

function getPanelData(record, isAuto, candidatesById) {
  const buckets = candidatesById?.[record.id] ?? {
    eph: { hcps: [], hcos: [] },
    vendor: { hcps: [], hcos: [] },
  };
  return {
    hcps: [...(buckets.eph?.hcps || []), ...(buckets.vendor?.hcps || [])],
    hcos: [...(buckets.eph?.hcos || []), ...(buckets.vendor?.hcos || [])],
    sourceBuckets: buckets,
  };
}

// *_selected_source carries the vendor name (e.g. "iqvia", "open-data"); only "eph" is EPH.
const normalizeSelectedSource = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  return normalized === "eph" ? "eph" : "vendor";
};

const resolveAutoSelectedIdx = (
  selectedResult,
  entityKey,
  selectedSource,
  candidates,
) => {
  const hasSelectedResult =
    !!selectedResult && typeof selectedResult === "object";
  const selectedRaw = hasSelectedResult ? selectedResult[entityKey] : null;
  if (selectedRaw && typeof selectedRaw === "object") {
    const source = normalizeSelectedSource(selectedSource);
    const selectedIds = new Set();
    (source ? [source] : ["eph", "vendor"]).forEach((src) => {
      (REVIEW_RESULT_FIELD_CONFIG[src]?.enterpriseId || []).forEach((path) => {
        const value = reviewReadByPath(selectedRaw, path);
        if (value !== undefined && value !== null && value !== "") {
          selectedIds.add(String(value));
        }
      });
    });
    return candidates.findIndex(
      (c) =>
        selectedIds.has(String(c.enterpriseId)) &&
        (!source || c.sourceType === source),
    );
  }
  if (hasSelectedResult) return -1;
  return candidates.length === 1 ? 0 : -1;
};

const reviewReadByPath = (obj, path) => {
  if (!obj || !path) {
    return undefined;
  }
  return path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const reviewFirstDefinedValue = (obj, paths) => {
  for (const path of paths || []) {
    const value = reviewReadByPath(obj, path);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const getReviewMappedField = (
  item,
  sourceType,
  fieldName,
  fallbackValue = undefined,
) => {
  const config =
    REVIEW_RESULT_FIELD_CONFIG[sourceType] || REVIEW_RESULT_FIELD_CONFIG.eph;
  const configuredValue = reviewFirstDefinedValue(item, config[fieldName]);

  if (configuredValue !== undefined) {
    return configuredValue;
  }

  if (sourceType === "vendor") {
    const vendorEntity =
      item?.entity && typeof item.entity === "object" ? item.entity : null;
    const entityConfiguredValue = reviewFirstDefinedValue(
      vendorEntity,
      config[fieldName],
    );

    if (entityConfiguredValue !== undefined) {
      return entityConfiguredValue;
    }

    if (fieldName === "name") {
      const derivedName =
        `${item?.individual?.firstName || vendorEntity?.first_name__v || ""} ${item?.individual?.lastName || vendorEntity?.last_name__v || ""}`.trim();
      if (derivedName) {
        return derivedName;
      }
      return (
        item?.activity?.activityEid ||
        item?.onekeyEid ||
        item?.entityId ||
        vendorEntity?.vid__v ||
        vendorEntity?.veevaid__v ||
        fallbackValue
      );
    }
    if (fieldName === "country") {
      return (
        item?.country ||
        vendorEntity?.primary_country__v ||
        vendorEntity?.country_cda__v ||
        vendorEntity?.country__v ||
        fallbackValue
      );
    }
    if (fieldName === "primarySpecialty") {
      return (
        vendorEntity?.specialty_1__v ||
        item?.individual?.typeLabel ||
        vendorEntity?.spec_1_local__v ||
        vendorEntity?.spec_2_local__v ||
        item?.activity?.statusLabel ||
        fallbackValue
      );
    }
    if (fieldName === "classificationCode") {
      return (
        vendorEntity?.hcp_type__v ||
        vendorEntity?.hco_type__v ||
        item?.individual?.typeLabel ||
        item?.entityType ||
        vendorEntity?.entityType ||
        vendorEntity?.hcp_type_local__v ||
        vendorEntity?.hco_type_local__v ||
        fallbackValue
      );
    }
    if (fieldName === "type") {
      return (
        item?.entityType ||
        vendorEntity?.entityType ||
        vendorEntity?.hco_type_local__v ||
        vendorEntity?.hcp_type_local__v ||
        fallbackValue
      );
    }
  }

  return fallbackValue;
};

const getReviewVendorAddress = (item) => {
  const vendorEntity =
    item?.entity && typeof item.entity === "object" ? item.entity : null;
  const workplaceAddresses = item?.workplace?.workplaceAddresses;
  if (!workplaceAddresses || typeof workplaceAddresses !== "object") {
    const entityAddresses = vendorEntity?.addresses__v;
    if (!Array.isArray(entityAddresses) || entityAddresses.length === 0) {
      return (
        vendorEntity?.formatted_address__v ||
        vendorEntity?.street_address_1__v ||
        ""
      );
    }

    const selectedAddress =
      entityAddresses.find(
        (address) => Number(address?.address_ordinal__v) === 1,
      ) ||
      entityAddresses.find(
        (address) =>
          String(address?.primary_cda__v || "").toUpperCase() === "Y",
      ) ||
      entityAddresses[0] ||
      {};

    return (
      selectedAddress?.formatted_address__v ||
      selectedAddress?.address_line_1__v ||
      selectedAddress?.street_address_1__v ||
      selectedAddress?.delivery_address__v ||
      ""
    );
  }

  const addresses = Object.values(workplaceAddresses);
  const selected =
    addresses.find((entry) => Number(entry?.rank) === 1) || addresses[0] || {};
  const longLabel = selected?.address?.addressLongLabel || "";
  const village = selected?.address?.postalTownReference?.villageLabel || "";

  return `${longLabel} ${village}`.trim();
};

const getReviewEntityMatches = (searchResults, sourceKey, resultKey) => {
  const sourceBucket = searchResults?.[sourceKey] || {};
  const rawValue =
    sourceBucket?.[resultKey] ||
    sourceBucket?.[resultKey?.toUpperCase?.()] ||
    sourceBucket?.[resultKey?.toLowerCase?.()] ||
    [];

  if (Array.isArray(rawValue)) {
    return rawValue;
  }
  if (!rawValue || typeof rawValue !== "object") {
    return [];
  }
  if (Array.isArray(rawValue.matches)) return rawValue.matches;
  if (Array.isArray(rawValue.results)) return rawValue.results;
  if (Array.isArray(rawValue.data)) return rawValue.data;
  if (Array.isArray(rawValue.records)) return rawValue.records;
  return [rawValue];
};

const valueOrUndefined = (value) =>
  value && value !== "N/A" ? value : undefined;

const normalizeReviewEntityFields = (item, sourceType, kind = "hcp") => {
  const fields = {
    enterpriseId:
      getReviewMappedField(item, sourceType, "enterpriseId") || "N/A",
    name: getReviewMappedField(item, sourceType, "name") || "N/A",
    primarySpeciality:
      getReviewMappedField(item, sourceType, "primarySpecialty") || "N/A",
    address:
      (sourceType === "vendor"
        ? getReviewVendorAddress(item)
        : item?.Address) || "N/A",
    city: getReviewMappedField(item, sourceType, "country") || "N/A",
    licenseId: item?.identifier_1_id || item?.LicenseId || "N/A",
    classificationCode:
      getReviewMappedField(item, sourceType, "classificationCode") || "N/A",
  };

  const rawLikeEntity =
    kind === "hco"
      ? {
          enterprise_id: valueOrUndefined(fields.enterpriseId),
          name: valueOrUndefined(fields.name),
          classification_code: valueOrUndefined(fields.classificationCode),
          primary_specialty: valueOrUndefined(fields.primarySpeciality),
          hco_adrs_line_1: valueOrUndefined(fields.address),
          hco_adrs_city: valueOrUndefined(fields.city),
        }
      : {
          enterprise_id: valueOrUndefined(fields.enterpriseId),
          first_name: valueOrUndefined(
            getReviewMappedField(item, sourceType, "firstName"),
          ),
          last_name: valueOrUndefined(
            getReviewMappedField(item, sourceType, "lastName"),
          ),
          classification_code: valueOrUndefined(fields.classificationCode),
          primary_specialty: valueOrUndefined(fields.primarySpeciality),
          identifier_1_id: valueOrUndefined(fields.licenseId),
          hcp_adrs_line_1: valueOrUndefined(fields.address),
          hcp_adrs_city: valueOrUndefined(fields.city),
        };

  return {
    ...fields,
    ...(buildAutoEntitySummary(
      kind === "hco" ? "HCO" : "HCP",
      rawLikeEntity,
    ) || {
      lines: [],
      hiddenCount: 0,
      hoverDetails: "",
    }),
    sourceType,
    raw: item,
  };
};

const normalizeAutoFieldValue = (value) => {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return String(value).trim();
};

const buildAutoEntitySummary = (entityType, entity) => {
  const config = AUTO_ENTITY_FIELD_CONFIG[entityType];
  if (!entity || !config) return null;

  const availableLines = config.fields
    .map(([key, label]) => {
      const value = normalizeAutoFieldValue(entity?.[key]);
      return value ? { key: label, value } : null;
    })
    .filter(Boolean);

  if (availableLines.length === 0) return null;

  return {
    lines: availableLines.slice(0, AUTO_PREVIEW_FIELD_COUNT),
    hiddenCount: Math.max(0, availableLines.length - AUTO_PREVIEW_FIELD_COUNT),
    hoverDetails: availableLines.map((l) => `${l.key}: ${l.value}`).join("\n"),
  };
};

const buildAutoInputEntity = (inputEntity, kind) => {
  if (
    !inputEntity ||
    typeof inputEntity !== "object" ||
    Object.keys(inputEntity).length === 0
  ) {
    return null;
  }

  const summary = buildAutoEntitySummary(
    kind === "hcp" ? "HCP" : kind === "parent_hco" ? "PARENT_HCO" : "HCO",
    inputEntity,
  );
  if (!summary) return null;

  if (kind === "hcp") {
    const name =
      `${inputEntity.first_name || ""} ${inputEntity.last_name || ""}`.trim() ||
      inputEntity.name ||
      "N/A";
    return {
      enterpriseId: inputEntity.enterprise_id || "N/A",
      name,
      primarySpeciality: inputEntity.primary_specialty || "N/A",
      address:
        reviewFirstDefinedValue(inputEntity, [
          "hcp_adrs_line_1",
          "hcp_adrs_line_2",
        ]) || "N/A",
      city: inputEntity.hcp_adrs_city || "N/A",
      licenseId: inputEntity.identifier_1_id || "N/A",
      classificationCode: inputEntity.classification_code || "N/A",
      ...summary,
      sourceType: "input",
      raw: inputEntity,
    };
  }

  return {
    enterpriseId:
      reviewFirstDefinedValue(inputEntity, [
        "enterprise_id",
        "rel_hco_enterprise_id",
      ]) || "N/A",
    name:
      reviewFirstDefinedValue(inputEntity, ["name", "rel_hco_name"]) || "N/A",
    primarySpeciality: inputEntity.primary_specialty || "N/A",
    address:
      reviewFirstDefinedValue(inputEntity, [
        "hco_adrs_line_1",
        "hco_adrs_line_2",
        "rel_hco_adrs_line_1",
        "rel_hco_adrs_line_2",
      ]) || "N/A",
    city:
      reviewFirstDefinedValue(inputEntity, [
        "hco_adrs_city",
        "rel_hco_adrs_city",
      ]) || "N/A",
    classificationCode:
      reviewFirstDefinedValue(inputEntity, [
        "classification_code",
        "rel_hco_clsfctn_cd",
      ]) || "N/A",
    ...summary,
    sourceType: "input",
    raw: inputEntity,
  };
};

const normalizeReviewVendorSearchStatus = (entry) => {
  const statusSource =
    entry?.vendorSearchStatus ||
    entry?.vendor_search_status ||
    entry?.vendorSearch ||
    entry?.vendor_search ||
    {};

  return {
    opted_for_hcp_vendor_search: !!(
      statusSource?.opted_for_hcp_vendor_search ??
      entry?.opted_for_hcp_vendor_search
    ),
    hcp_vendor_search_status:
      statusSource?.hcp_vendor_search_status ??
      entry?.hcp_vendor_search_status ??
      "",
    opted_for_hco_vendor_search: !!(
      statusSource?.opted_for_hco_vendor_search ??
      entry?.opted_for_hco_vendor_search
    ),
    hco_vendor_search_status:
      statusSource?.hco_vendor_search_status ??
      entry?.hco_vendor_search_status ??
      "",
    opted_for_parent_hco_vendor_search: !!(
      statusSource?.opted_for_parent_hco_vendor_search ??
      entry?.opted_for_parent_hco_vendor_search
    ),
    parent_hco_vendor_search_status:
      statusSource?.parent_hco_vendor_search_status ??
      entry?.parent_hco_vendor_search_status ??
      "",
  };
};

const mapReviewApiResponse = (
  rawEntries,
  rowOffset = 0,
  expeditedFlow = true,
) => {
  const eph = [];
  const vendor = [];
  const auto = [];
  const notFound = [];
  const candidatesById = {};
  const vendorStatusById = {};
  const savedSelectionById = {};

  (Array.isArray(rawEntries) ? rawEntries : []).forEach((entry, index) => {
    const rowNumber = rowOffset + index + 1;
    const rawSource = String(entry?.source || "")
      .trim()
      .toLowerCase();
    const normalizedSource = REVIEW_SOURCE_ALIASES[rawSource] || "";
    const count = Number(entry?.count || 0);
    const isNotFoundSource =
      rawSource === "not_found" || rawSource === "notfound";
    const isAutoProcessed =
      expeditedFlow &&
      (String(entry?.status || "")
        .trim()
        .toUpperCase() === "AUTO_PROCESSED" ||
        isNotFoundSource);
    const effectiveSource = normalizedSource || (isAutoProcessed ? "eph" : "");
    const hcpSource =
      REVIEW_SOURCE_ALIASES[
        String(entry?.hcp_source || "")
          .trim()
          .toLowerCase()
      ] || effectiveSource;
    const hcoSource =
      REVIEW_SOURCE_ALIASES[
        String(entry?.hco_source || "")
          .trim()
          .toLowerCase()
      ] || effectiveSource;
    const parentHcoSource =
      REVIEW_SOURCE_ALIASES[
        String(entry?.parent_hco_source || "")
          .trim()
          .toLowerCase()
      ] || effectiveSource;

    if (!isAutoProcessed && expeditedFlow) {
      if (count === 0) return;
      if (normalizedSource !== "eph" && normalizedSource !== "vendor") return;
    }

    const hcpMatches = getReviewEntityMatches(
      entry?.search_results,
      hcpSource,
      "hcp",
    );
    const hcoMatches = getReviewEntityMatches(
      entry?.search_results,
      hcoSource,
      "hco",
    );
    const parentHcoMatches = getReviewEntityMatches(
      entry?.search_results,
      parentHcoSource,
      "parent_hco",
    );

    const hcpInputRaw = entry?.input?.hcp || entry?.input?.HCP;
    const hcoInputRaw = entry?.input?.hco || entry?.input?.HCO;
    const parentHcoInputRaw =
      entry?.input?.parent_hco || entry?.input?.Parent_HCO;
    const hasHcpInput =
      !!hcpInputRaw &&
      typeof hcpInputRaw === "object" &&
      Object.keys(hcpInputRaw).length > 0;
    const hasHcoInput =
      !!hcoInputRaw &&
      typeof hcoInputRaw === "object" &&
      Object.keys(hcoInputRaw).length > 0;
    const hasParentHcoInput =
      !!parentHcoInputRaw &&
      typeof parentHcoInputRaw === "object" &&
      Object.keys(parentHcoInputRaw).length > 0;

    const isSingleEntityInput =
      (hasHcpInput && !hasHcoInput && !hasParentHcoInput) ||
      (hasHcoInput && !hasHcpInput && !hasParentHcoInput);
    const showRightEntity = !isSingleEntityInput;
    const leftEntityType = hasHcoInput && !hasHcpInput ? "hco" : "hcp";
    const rightEntityType = hasParentHcoInput ? "parent_hco" : "hco";

    const leftMatches = leftEntityType === "hco" ? hcoMatches : hcpMatches;
    const leftSource = leftEntityType === "hco" ? hcoSource : hcpSource;
    const leftInputRaw = leftEntityType === "hco" ? hcoInputRaw : hcpInputRaw;
    const hasLeftInput = leftEntityType === "hco" ? hasHcoInput : hasHcpInput;

    const rightMatches = showRightEntity
      ? rightEntityType === "parent_hco"
        ? parentHcoMatches
        : hcoMatches
      : [];
    const rightSource =
      rightEntityType === "parent_hco" ? parentHcoSource : hcoSource;
    const rightInputRaw =
      rightEntityType === "parent_hco" ? parentHcoInputRaw : hcoInputRaw;
    const hasRightInput =
      showRightEntity &&
      (rightEntityType === "parent_hco" ? hasParentHcoInput : hasHcoInput);

    if (
      !isAutoProcessed &&
      expeditedFlow &&
      leftMatches.length + rightMatches.length === 0
    ) {
      return;
    }
    const isNotFoundRow =
      !expeditedFlow &&
      (count === 0 ||
        (normalizedSource !== "eph" && normalizedSource !== "vendor") ||
        leftMatches.length + rightMatches.length === 0);

    const hcps = leftMatches.map((item) =>
      normalizeReviewEntityFields(item, leftSource, leftEntityType),
    );
    const hcos = rightMatches.map((item) =>
      normalizeReviewEntityFields(item, rightSource, "hco"),
    );
    const idPrefix = isNotFoundRow
      ? "NF"
      : isAutoProcessed
        ? "AUTO"
        : normalizedSource === "vendor"
          ? "VND"
          : "EPH";
    const id = `${idPrefix}-${rowNumber}`;

    const ephHcpMatches = getReviewEntityMatches(
      entry?.search_results,
      "eph",
      "hcp",
    );
    const ephHcoRaw = getReviewEntityMatches(
      entry?.search_results,
      "eph",
      "hco",
    );
    const ephParentHcoRaw = getReviewEntityMatches(
      entry?.search_results,
      "eph",
      "parent_hco",
    );
    const ephLeftMatches = leftEntityType === "hco" ? ephHcoRaw : ephHcpMatches;
    const ephRightMatches = showRightEntity
      ? rightEntityType === "parent_hco"
        ? ephParentHcoRaw
        : ephHcoRaw
      : [];
    const vendorHcpMatches = getReviewEntityMatches(
      entry?.search_results,
      "vendor",
      "hcp",
    );
    const vendorHcoRaw = getReviewEntityMatches(
      entry?.search_results,
      "vendor",
      "hco",
    );
    const vendorParentHcoRaw = getReviewEntityMatches(
      entry?.search_results,
      "vendor",
      "parent_hco",
    );
    const vendorLeftMatches =
      leftEntityType === "hco" ? vendorHcoRaw : vendorHcpMatches;
    const vendorRightMatches = showRightEntity
      ? rightEntityType === "parent_hco"
        ? vendorParentHcoRaw
        : vendorHcoRaw
      : [];

    let inputHcp = null;
    let inputRight = null;
    if (showRightEntity) {
      inputRight = buildAutoInputEntity(rightInputRaw, rightEntityType);
    }
    inputHcp = buildAutoInputEntity(leftInputRaw, leftEntityType);

    const rawHcpSource = String(entry?.hcp_source || entry?.source || "")
      .trim()
      .toLowerCase();
    const rawHcoSource = String(entry?.hco_source || entry?.source || "")
      .trim()
      .toLowerCase();
    const rawParentHcoSource = String(
      entry?.parent_hco_source || entry?.source || "",
    )
      .trim()
      .toLowerCase();
    const isSourceNotFound = (value) =>
      value === "not_found" || value === "notfound";
    const rawLeftSource =
      leftEntityType === "hco" ? rawHcoSource : rawHcpSource;
    const rawRightSource =
      rightEntityType === "parent_hco" ? rawParentHcoSource : rawHcoSource;
    const isHcpNotFound =
      isAutoProcessed && hasLeftInput && isSourceNotFound(rawLeftSource);
    const isRightNotFound =
      isAutoProcessed && hasRightInput && isSourceNotFound(rawRightSource);

    const hcp = inputHcp || hcps[0] || null;
    const hco = isNotFoundRow
      ? inputRight
      : rightEntityType === "parent_hco"
        ? inputRight || hcos[0] || null
        : hcos[0] || inputRight || null;

    const record = {
      id,
      pk: entry?.pk || entry?.Pk || entry?.PK || null,
      inputRowNumber: firstFilled(
        entry?.input?.input_row_number,
        hcpInputRaw?.input_row_number,
        hcoInputRaw?.input_row_number,
        parentHcoInputRaw?.input_row_number,
        entry?.input_row_number,
      ),
      leftEntityType,
      rightEntityType,
      type: hcp && hco ? "hcp_hco" : hcp ? "hcp_only" : "hco_only",
      hcp,
      hco,
      hcpNotFound: isHcpNotFound,
      hcoNotFound: isRightNotFound,
      parentHcoAlreadyExists:
        isAutoProcessed &&
        String(entry?.parent_hco_source || "")
          .trim()
          .toLowerCase() === "already_exist",
      hasHcp: hasLeftInput,
      hasHco: hasRightInput,
      isNotFoundRecord: isNotFoundRow,
      inputEntities: {
        hcp: hcpInputRaw,
        hco: hcoInputRaw,
        parentHco: parentHcoInputRaw,
      },
      sourceType: isNotFoundSource
        ? "not_found"
        : normalizedSource === "vendor"
          ? "vendor"
          : "eph",
      resultDetail: isAutoProcessed
        ? entry?.resultDetail || STATIC_AUTO_RESULT_DETAIL
        : undefined,
    };

    candidatesById[id] = {
      eph: {
        hcps: ephLeftMatches.map((item) =>
          normalizeReviewEntityFields(item, "eph", leftEntityType),
        ),
        hcos: ephRightMatches.map((item) =>
          normalizeReviewEntityFields(item, "eph", "hco"),
        ),
      },
      vendor: {
        hcps: vendorLeftMatches.map((item) =>
          normalizeReviewEntityFields(item, "vendor", leftEntityType),
        ),
        hcos: vendorRightMatches.map((item) =>
          normalizeReviewEntityFields(item, "vendor", "hco"),
        ),
      },
    };
    if (isNotFoundRow) {
      candidatesById[id] = {
        eph: { hcps: [], hcos: [] },
        vendor: { hcps: [], hcos: [] },
      };
    }
    if (isAutoProcessed) {
      const selectedResult = entry?.selected_result || entry?.selectedResult;
      const bucket = candidatesById[id];
      record.autoSelection = {
        hcpIdx: resolveAutoSelectedIdx(
          selectedResult,
          leftEntityType,
          leftEntityType === "hco"
            ? entry?.hco_selected_source
            : entry?.hcp_selected_source,
          [...bucket.eph.hcps, ...bucket.vendor.hcps],
        ),
        hcoIdx: resolveAutoSelectedIdx(
          selectedResult,
          rightEntityType,
          rightEntityType === "parent_hco"
            ? entry?.parent_hco_selected_source
            : entry?.hco_selected_source,
          [...bucket.eph.hcos, ...bucket.vendor.hcos],
        ),
      };
    }
    if (!isAutoProcessed) {
      const savedResult = entry?.selected_result ?? entry?.selectedResult;
      if (savedResult !== undefined && savedResult !== null) {
        const bucket = candidatesById[id];
        const hasSavedPick = typeof savedResult === "object";
        savedSelectionById[id] = hasSavedPick
          ? {
              hcpIdx: resolveAutoSelectedIdx(
                savedResult,
                leftEntityType,
                leftEntityType === "hco"
                  ? entry?.hco_selected_source
                  : entry?.hcp_selected_source,
                [...bucket.eph.hcps, ...bucket.vendor.hcps],
              ),
              hcoIdx: resolveAutoSelectedIdx(
                savedResult,
                rightEntityType,
                rightEntityType === "parent_hco"
                  ? entry?.parent_hco_selected_source
                  : entry?.hco_selected_source,
                [...bucket.eph.hcos, ...bucket.vendor.hcos],
              ),
            }
          : { hcpIdx: -1, hcoIdx: -1 };
      }
    }
    const vendorSearchStatus = normalizeReviewVendorSearchStatus(entry);
    const entityTypeStatus = {};
    if (vendorSearchStatus.opted_for_hcp_vendor_search) {
      entityTypeStatus.hcp = (
        vendorSearchStatus.hcp_vendor_search_status || "COMPLETED"
      ).toUpperCase();
    }
    if (vendorSearchStatus.opted_for_hco_vendor_search) {
      entityTypeStatus.hco = (
        vendorSearchStatus.hco_vendor_search_status || "COMPLETED"
      ).toUpperCase();
    }
    if (vendorSearchStatus.opted_for_parent_hco_vendor_search) {
      entityTypeStatus.parent_hco = (
        vendorSearchStatus.parent_hco_vendor_search_status || "COMPLETED"
      ).toUpperCase();
    }
    if (Object.keys(entityTypeStatus).length > 0) {
      vendorStatusById[id] = entityTypeStatus;
    }

    if (isAutoProcessed) {
      auto.push(record);
    } else if (isNotFoundRow) {
      notFound.push(record);
    } else if (normalizedSource === "vendor") {
      vendor.push(record);
    } else {
      eph.push(record);
    }
  });

  return {
    eph,
    vendor,
    auto,
    notFound,
    candidatesById,
    vendorStatusById,
    savedSelectionById,
  };
};

function getDcrRightLabel(rightEntityType) {
  return rightEntityType === "parent_hco" ? "Parent HCO" : "HCO";
}

function getDcrLeftLabel(leftEntityType) {
  return leftEntityType === "hco" ? "HCO" : "HCP";
}

function getDcrActionMessage(record, sel, pd) {
  const hasHcp = !!record.hcp;
  const hasRight = !!record.hco;
  const isHcpSelected = sel?.selectedHCPId
    ? true
    : (sel?.hcpIdx ?? -1) >= 0 && !!pd.hcps[sel.hcpIdx];
  const isRightSelected = sel?.selectedHCOId
    ? true
    : (sel?.hcoIdx ?? -1) >= 0 && !!pd.hcos[sel.hcoIdx];
  const leftLabel = getDcrLeftLabel(record.leftEntityType);
  const rightLabel = getDcrRightLabel(record.rightEntityType);

  if (hasHcp && hasRight) {
    if (isHcpSelected && isRightSelected)
      return `Creates a DCR for Existing ${leftLabel} with Existing ${rightLabel}`;
    if (!isHcpSelected && !isRightSelected)
      return `Creates a DCR for New ${leftLabel} with New ${rightLabel}`;
    if (isHcpSelected && !isRightSelected)
      return `Creates a DCR for Existing ${leftLabel} with New ${rightLabel}`;
    return `Creates a DCR for New ${leftLabel} with Existing ${rightLabel}`;
  }

  if (hasHcp && !hasRight) {
    return isHcpSelected
      ? `Creates a DCR for Edit ${leftLabel}`
      : `Creates a DCR for New ${leftLabel}`;
  }

  if (hasRight && !hasHcp) {
    return isRightSelected
      ? `Creates a DCR for Edit ${rightLabel}`
      : `Creates a DCR for New ${rightLabel}`;
  }

  return "";
}

const pickSummaryNumber = (obj, keys) => {
  for (const key of keys) {
    const v = obj?.[key];
    if (
      v !== undefined &&
      v !== null &&
      v !== "" &&
      Number.isFinite(Number(v))
    ) {
      return Number(v);
    }
  }
  return undefined;
};

const extractAutoSummary = (data) => {
  const found = [
    data?.summary,
    data?.Summary,
    data?.data?.summary,
    data?.results?.summary,
  ].find((c) => c && typeof c === "object");
  if (!found) return null;
  return {
    total: pickSummaryNumber(found, ["total", "total_records", "total_count"]),
    processed: pickSummaryNumber(found, [
      "processed",
      "processed_records",
      "total_processed",
      "processed_count",
    ]),
    failed: pickSummaryNumber(found, [
      "failed",
      "failed_records",
      "total_failed",
      "failed_count",
    ]),
    in_progress: pickSummaryNumber(found, [
      "in_progress",
      "inprogress",
      "in_progress_records",
      "inprogress_records",
    ]),
    pending: pickSummaryNumber(found, [
      "pending",
      "pending_records",
      "pending_count",
    ]),
  };
};

const normalizeProgressStatus = (value) => {
  const s = String(value || "")
    .trim()
    .toLowerCase();
  const squashed = s.replace(/[_\s-]/g, "");
  if (squashed === "inprogress") return "in_progress";
  if (squashed === "notapplicable") return "not_applicable";
  return s;
};

const firstFilled = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");

const parseResultObject = (value) => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
      return null;
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : null;
};

const toBoolOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "boolean") return value;
  return ["true", "1", "yes", "success"].includes(
    String(value).trim().toLowerCase(),
  );
};

const boolLabel = (value, yes, no) => {
  const b = toBoolOrNull(value);
  return b === null ? undefined : b ? yes : no;
};

const pushRow = (rows, label, value, mono) => {
  if (value === undefined || value === null || value === "") return;
  rows.push({ label, value, mono });
};

const buildDcrDetail = (rec) => {
  const payload = parseResultObject(rec?.dcr_result) || {};
  const shell = parseResultObject(rec?.shell_profile_result) || {};
  const rows = [];
  const vendorLabel = (v) =>
    String(v || "-").toUpperCase() === "EPH" ? "Lilly Managed" : v || "-";

  const processedStatus = firstFilled(
    payload.processed_status,
    payload.processedStatus,
  );
  const hasErrorKey = payload.error !== undefined && payload.error !== null;
  const hasError = hasErrorKey
    ? toBoolOrNull(payload.error) === true
    : String(processedStatus || "").trim().toLowerCase() === "failed";

  if (hasError) {
    pushRow(rows, "Status", "Failed");
    pushRow(rows, "Error Type", firstFilled(payload.error_type, payload.errorType));
    const message = firstFilled(
      payload.error_message,
      payload.errorMessage,
      !hasErrorKey ? "Failed to process the DCR." : undefined,
    );
    return { rows, error: message };
  }

  const items = [
    ...(Array.isArray(payload.dcr_ids) ? payload.dcr_ids : []),
    ...(Array.isArray(payload.results) ? payload.results : []),
  ]
    .map((item) => ({
      id: firstFilled(item?.change_request_id, item?.changeRequestId, item?.id),
      vendor: firstFilled(item?.vendor, item?.source),
    }))
    .filter((item) => item.id);
  const listed = items.length > 0 ? items : rec?.dcr_id ? [{ id: rec.dcr_id }] : [];
  listed.forEach((item, i) =>
    pushRow(rows, `DCR Id ${i + 1}`, `${item.id} (${vendorLabel(item.vendor)})`, true),
  );

  const pick = (...keys) =>
    firstFilled(...keys.flatMap((k) => [payload[k], shell[k]]));
  pushRow(
    rows,
    "HCP Enterprise ID",
    pick("HcpEnterpriseId", "HCPEnterpriseId", "hcpEnterpriseId", "hcp_enterprise_id"),
    true,
  );
  pushRow(
    rows,
    "HCO Enterprise ID",
    pick("HcoEnterpriseId", "HCOEnterpriseId", "hcoEnterpriseId", "hco_enterprise_id"),
    true,
  );
  pushRow(rows, "Status", processedStatus);
  pushRow(rows, "Validation", firstFilled(payload.validation_status, payload.validationStatus));
  if (rows.length === 0) pushRow(rows, "Details", "No DCR details available");
  return { rows, error: firstFilled(rec?.dcr_error, rec?.dcr_error_message) };
};

const buildSubscriptionDetail = (rec) => {
  const sp = parseResultObject(rec?.shell_profile_result) || {};
  const rows = [];
  const spStatus = toBoolOrNull(
    firstFilled(
      rec?.shell_profile_status,
      sp.shell_profile_status,
      sp.shellProfileStatus,
      sp.Status,
      sp.status,
    ),
  );

  const rawErrors = firstFilled(sp.errors, sp.error, sp.error_message, sp.errorMessage);
  const errors = Array.isArray(rawErrors)
    ? rawErrors.filter(Boolean).map(String)
    : rawErrors && typeof rawErrors === "object"
      ? Object.entries(rawErrors).map(([k, v]) => `${k}: ${v}`)
      : rawErrors
        ? [String(rawErrors)]
        : [];

  if (spStatus === false || errors.length > 0) {
    pushRow(rows, "Status", "Failed");
    return {
      rows,
      error: errors.length > 0 ? errors.join("\n") : "Shell profile failed.",
    };
  }

  const sourceLabel = (s) => {
    const v = String(s || "").trim().toLowerCase();
    return v === "vendor" ? "IQVIA" : v ? v.toUpperCase() : "-";
  };
  [
    ["HCP", ["Hcp", "HCP", "hcp"], "hcp", rec?.hcp_selected_source ?? rec?.hcp_source ?? rec?.source],
    ["HCO", ["Hco", "HCO", "hco"], "hco", rec?.hco_selected_source ?? rec?.hco_source ?? rec?.source],
    [
      "Parent HCO",
      ["ParentHco", "ParentHCO", "parentHco", "parent_hco"],
      "parent_hco",
      rec?.parent_hco_selected_source ?? rec?.hco_selected_source ?? rec?.source,
    ],
  ].forEach(([label, prefixes, statusPrefix, source]) => {
    const statusKeys = [
      `${statusPrefix}_subscription_status`,
      ...prefixes.map((p) => `${p}SubscriptionStatus`),
    ];
    const enabled = toBoolOrNull(
      firstFilled(...statusKeys.flatMap((k) => [rec?.[k], sp[k]])),
    );
    const pick = (suffixes) =>
      firstFilled(...prefixes.flatMap((p) => suffixes.map((s) => sp[`${p}${s}`])));
    const hasIds =
      pick(["EnterpriseId", "_enterprise_id"]) !== undefined ||
      pick(["EntityId", "_entity_id"]) !== undefined;
    if (enabled === false || (!enabled && !hasIds)) return;
    if (label !== "HCP") pushRow(rows, `${label} Source`, sourceLabel(source));
    pushRow(rows, `${label} Enterprise ID`, pick(["EnterpriseId", "_enterprise_id"]) ?? "-", true);
    pushRow(rows, `${label} Entity ID`, pick(["EntityId", "_entity_id"]) ?? "-", true);
    pushRow(
      rows,
      `${label} OneKey ID Found In EPH`,
      toBoolOrNull(pick(["OneKeyIdFoundInEPH", "_one_key_id_found_in_eph"])) ? "Yes" : "No",
    );
  });
  if (rows.length === 0) {
    pushRow(rows, "Details", "No subscription details available.");
  }
  return { rows, error: firstFilled(rec?.subscription_error, rec?.subscription_error_message) };
};

const buildConsentDetail = (rec) => {
  const cp = parseResultObject(rec?.consent_result);
  const rows = [];
  pushRow(
    rows,
    "Consent Uploaded At",
    firstFilled(cp?.consent_uploaded_at, cp?.consentUploadedAt, rec?.consent_uploaded_at),
    true,
  );
  pushRow(rows, "Email Consent Status", boolLabel(firstFilled(cp?.email_consent_status, cp?.emailConsentStatus), "Success", "Failed"));
  pushRow(rows, "Phone Consent Status", boolLabel(firstFilled(cp?.phone_consent_status, cp?.phoneConsentStatus), "Success", "Failed"));
  pushRow(
    rows,
    "WhatsApp Consent Status",
    boolLabel(
      firstFilled(
        cp?.whatsapp_consent_status,
        cp?.whatsappConsentStatus,
        rec?.whatsapp_consent_status,
        rec?.whatsappConsentStatus,
      ),
      "Success",
      "Failed",
    ),
  );
  const emailError = firstFilled(cp?.email_consent_error, cp?.emailConsentError);
  const phoneError = firstFilled(cp?.phone_consent_error, cp?.phoneConsentError);
  if (emailError) pushRow(rows, "Email Consent Error", emailError);
  if (phoneError) pushRow(rows, "Phone Consent Error", phoneError);
  if (rows.length === 0) pushRow(rows, "Details", "No consent details available.");

  const error = emailError || phoneError
    ? undefined
    : firstFilled(rec?.consent_error, rec?.consent_error_message);
  return { rows, error };
};

const buildProgressDetail = (rec) => {
  const dcr = buildDcrDetail(rec);
  const subscription = buildSubscriptionDetail(rec);
  const consent = buildConsentDetail(rec);
  return {
    recordStatus: normalizeProgressStatus(rec?.record_status),
    dcr: {
      status: normalizeProgressStatus(rec?.dcr_progress_status),
      ...dcr,
    },
    subscription: {
      status: normalizeProgressStatus(rec?.subscription_progress_status),
      ...subscription,
    },
    consent: {
      status: normalizeProgressStatus(rec?.consent_progress_status),
      ...consent,
    },
  };
};

function deriveResultStatus(d) {
  if (
    ["pending", "in_progress", "processed", "failed"].includes(d.recordStatus)
  ) {
    return d.recordStatus;
  }
  const s = [d.dcr.status, d.subscription.status, d.consent.status];
  if (s.some((x) => x === "in_progress")) return "in_progress";
  if (s.some((x) => x === "pending" || x === "applicable")) return "pending";
  if (s.some((x) => x === "failed")) return "failed";
  return "processed";
}

function rsClass(rs) {
  return {
    pending: "pend",
    in_progress: "prog",
    processed: "done",
    failed: "fail",
  }[rs];
}
function rsLabel(rs) {
  return {
    pending: "Pending",
    in_progress: "In Progress",
    processed: "Processed",
    failed: "Failed",
  }[rs];
}

function HCPCard({ hcp, isReviewed, isAutoSelected }) {
  return (
    <div className="hcp-card">
      <div className="card-top">
        <div className="card-ico">
          <IcoUser size={11} />
        </div>
        <span className="card-lbl">HCP</span>
        {(isReviewed || isAutoSelected) && (
          <span className="rc-reviewed-bdg">
            {isReviewed ? (
              <span className="right-tick-cs">✓</span>
            ) : (
              <AutoModeIcon sx={{ fontSize: "14px" }} />
            )}
            {isReviewed ? "Reviewed" : "Auto Selected"}
          </span>
        )}
      </div>
      {hcp.lines ? (
        <EntityFlds
          lines={hcp.lines}
          hiddenCount={hcp.hiddenCount}
          hoverDetails={hcp.hoverDetails}
        />
      ) : (
        <div className="flds">
          <Fld
            label="Enterprise Id"
            value={hcp.enterpriseId}
            mono
            accent="sky"
          />
          <Fld label="Name" value={hcp.name} bold />
          <Fld label="Primary Speciality" value={hcp.primarySpeciality} />
          <Fld label="Address" value={hcp.address} />
          <Fld label="City" value={hcp.city} />
          <Fld label="License Id" value={hcp.licenseId} mono />
          <Fld label="Classification Code" value={hcp.classificationCode} />
        </div>
      )}
    </div>
  );
}

function HCOCard({
  hco,
  label = "Parent HCO",
  isReviewed,
  isAutoSelected,
  hasExistingParentHco,
}) {
  return (
    <div className="hco-card">
      <div className="card-top">
        <div className="card-ico">
          <IcoBuilding2 size={11} />
        </div>
        <span className="card-lbl">{label}</span>
        {(isReviewed || isAutoSelected) && (
          <span className="rc-reviewed-bdg">
            {isReviewed ? (
              <span className="right-tick-cs">✓</span>
            ) : (
              <AutoModeIcon sx={{ fontSize: "14px" }} />
            )}
            {isReviewed ? "Reviewed" : "Auto Selected"}
          </span>
        )}
      </div>
      {hco.lines ? (
        <EntityFlds
          lines={hco.lines}
          hiddenCount={hco.hiddenCount}
          hoverDetails={hco.hoverDetails}
        />
      ) : (
        <div className="flds">
          <Fld
            label="Enterprise Id"
            value={hco.enterpriseId}
            mono
            accent="em"
          />
          <Fld label="Name" value={hco.name} bold />
          <Fld label="Address" value={hco.address} />
          <Fld label="City" value={hco.city} />
          <Fld label="Classification Code" value={hco.classificationCode} />
        </div>
      )}
      {hasExistingParentHco && <ParentHcoNotice />}
    </div>
  );
}

function ParentHcoNotice() {
  return (
    <div className="rc-parent-hco-notice">
      The given HCP has an existing parent HCO
    </div>
  );
}

function EntityFlds({ lines, hiddenCount, hoverDetails }) {
  return (
    <div className="flds">
      {lines.map((line, idx) => (
        <Fld
          key={line.key}
          label={line.key}
          value={line.value}
          mono={idx === 0}
          bold={idx === 0}
        />
      ))}
      {hiddenCount > 0 && (
        <div className="fld-more" title={hoverDetails}>
          <span className="fld-more-dots">•••</span>
          <span className="fld-more-count">{hiddenCount} more</span>
        </div>
      )}
    </div>
  );
}

function Fld({ label, value, mono, bold, accent }) {
  const cls = [
    "fld-v",
    mono ? "mono" : "",
    bold ? "bold" : "",
    accent === "sky" ? "sky" : "",
    accent === "em" ? "em" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="fld">
      <span className="fld-l">{label}</span>
      <span className={cls}>{value}</span>
    </div>
  );
}

function RecordRow({
  record,
  index,
  showStatus,
  onStatusClick,
  onRowClick,
  selectedHCPId,
  selectedHCOId,
  showCheckbox,
  isChecked,
  onToggleCheck,
  dcrActionMessage,
  isAutoProcessed,
  hcpAutoSelected,
  hcoAutoSelected,
}) {
  const d = record.resultDetail;
  const rs = d ? deriveResultStatus(d) : "processed";
  const rsc = rsClass(rs);

  const isFullyNotFound =
    isAutoProcessed &&
    (record.hasHcp || record.hasHco) &&
    (!record.hasHcp || !!record.hcpNotFound) &&
    (!record.hasHco || !!record.hcoNotFound);

  const handleRowClick = () => {
    if (isFullyNotFound || record.isNotFoundRecord) return;
    onRowClick?.(record);
  };

  return (
    <div
      className={`rc-rec${isChecked ? " chk" : ""}${
        isFullyNotFound ? " rc-rec-disabled" : ""
      }`}
      onClick={handleRowClick}
    >
      <div className="rc-rec-hdr">
        {showCheckbox && (
          <button
            className={`rc-chk-btn${isChecked ? " on" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCheck?.(record.id);
            }}
          >
            {isChecked ? <IcoSquareCheck /> : <IcoSquare />}
          </button>
        )}
        <span className="rc-rec-num">
          #{String(record.inputRowNumber ?? index + 1).padStart(2, "0")}
        </span>

        {showStatus && d && (
          <>
            <div className="rc-si">
              <StatusIcon status={d.dcr.status} />
              <span className="rc-si-lbl">DCR:</span>
              <StatusLabel status={d.dcr.status} />
            </div>
            <span className="rc-pipe">|</span>
            <div className="rc-si">
              <StatusIcon status={d.subscription.status} />
              <span className="rc-si-lbl">Subscription:</span>
              <StatusLabel status={d.subscription.status} />
            </div>
            <span className="rc-pipe">|</span>
            <div className="rc-si">
              <StatusIcon status={d.consent.status} />
              <span className="rc-si-lbl">Consents:</span>
              <StatusLabel status={d.consent.status} />
            </div>
            <button
              className={`rc-rpill ${rsc}`}
              onClick={(e) => {
                e.stopPropagation();
                onStatusClick?.(record);
              }}
            >
              <span className={`rc-pdot ${rsc}`} />
              Result Status: {rsLabel(rs)}
            </button>
          </>
        )}
      </div>

      <div className="rc-rec-body">
        {record.hcp &&
          (record.leftEntityType === "hco" ? (
            <HCOCard
              hco={record.hcp}
              label="HCO"
              isReviewed={
                !isAutoProcessed && !!selectedHCPId && !hcpAutoSelected
              }
              isAutoSelected={!!hcpAutoSelected}
            />
          ) : (
            <HCPCard
              hcp={record.hcp}
              hasExistingParentHco={record.parentHcoAlreadyExists}
              isReviewed={
                !isAutoProcessed && !!selectedHCPId && !hcpAutoSelected
              }
              isAutoSelected={!!hcpAutoSelected}
            />
          ))}
        {record.hco && (
          <HCOCard
            hco={record.hco}
            label={record.rightEntityType === "hco" ? "HCO" : "Parent HCO"}
            hasExistingParentHco={
              isAutoProcessed &&
              record.rightEntityType === "parent_hco" &&
              record.parentHcoAlreadyExists
            }
            isReviewed={!isAutoProcessed && !!selectedHCOId && !hcoAutoSelected}
            isAutoSelected={!!hcoAutoSelected}
          />
        )}
      </div>

      {(selectedHCPId ||
        selectedHCOId ||
        record.hcpNotFound ||
        record.hcoNotFound ||
        dcrActionMessage) && (
        <div className="rc-rec-foot">
          {isAutoProcessed && record.hcpNotFound ? (
            <div className="rc-sel-tag hcp not-found">
              {record.leftEntityType === "hco" ? (
                <IcoBuilding2 size={11} />
              ) : (
                <IcoUser size={11} />
              )}
              <span className="t-lbl">
                {record.leftEntityType === "hco" ? "HCO" : "HCP"}:
              </span>
              <span className="t-id">Not Found</span>
            </div>
          ) : (
            selectedHCPId && (
              <div className="rc-sel-tag hcp">
                {record.leftEntityType === "hco" ? (
                  <IcoBuilding2 size={11} />
                ) : (
                  <IcoUser size={11} />
                )}
                <span className="t-lbl">
                  Selected {record.leftEntityType === "hco" ? "HCO" : "HCP"}:
                </span>
                <span className="t-id">{selectedHCPId}</span>
              </div>
            )
          )}
          {isAutoProcessed && record.hcoNotFound ? (
            <div className="rc-sel-tag hco not-found">
              <IcoBuilding2 size={11} />
              <span className="t-lbl">
                {record.rightEntityType === "hco" ? "HCO" : "Parent HCO"}:
              </span>
              <span className="t-id">Not Found</span>
            </div>
          ) : (
            selectedHCOId && (
              <div className="rc-sel-tag hco">
                <IcoBuilding2 size={11} />
                <span className="t-lbl">
                  Selected{" "}
                  {record.rightEntityType === "hco" ? "HCO" : "Parent HCO"}:
                </span>
                <span className="t-id">{selectedHCOId}</span>
              </div>
            )
          )}
          {dcrActionMessage && (
            <div
              className={`rc-dcr-msg inline${isAutoProcessed ? " good" : ""}`}
            >
              {isAutoProcessed ? (
                <IcoCheckCircle2 size={13} />
              ) : (
                <IcoAlertCircle size={13} />
              )}
              <span>{dcrActionMessage}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Pagination({ page, total, perPage, onChange }) {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1))
      pages.push(i);
    else if (pages[pages.length - 1] !== "…") pages.push("…");
  }
  return (
    <div className="rc-pg">
      <p className="rc-pg-info">
        Showing{" "}
        <strong>
          {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)}
        </strong>{" "}
        of <strong>{total}</strong> records
      </p>
      <div className="rc-pg-ctrl">
        <button
          className="rc-pg-btn"
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
        >
          <IcoChevLeft />
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="rc-pg-ell">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`rc-pg-btn${p === page ? " on" : ""}`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          className="rc-pg-btn"
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
        >
          <IcoChevRight />
        </button>
      </div>
    </div>
  );
}

function RecordList({
  records,
  showStatus,
  onStatusClick,
  onRowClick,
  selections,
  isAutoProcessed,
  showCheckbox,
  checkedRecords,
  onToggleCheck,
  candidatesById,
  autoSelectEnabled,
}) {
  if (records.length === 0)
    return <div className="rc-empty">No records found.</div>;
  return (
    <div className="rc-recs">
      {records.map((record, i) => {
        const sel = selections?.[record.id];
        const pd = getPanelData(
          record,
          isAutoProcessed ?? false,
          candidatesById,
        );
        const autoPick =
          autoSelectEnabled && !isAutoProcessed
            ? {
                hcpIdx: pd.hcps.length === 1 ? 0 : -1,
                hcoIdx: pd.hcos.length === 1 ? 0 : -1,
              }
            : null;
        const hcpIdx = isAutoProcessed
          ? (record.autoSelection?.hcpIdx ?? -1)
          : (sel?.hcpIdx ?? autoPick?.hcpIdx ?? -1);
        const hcoIdx = isAutoProcessed
          ? (record.autoSelection?.hcoIdx ?? -1)
          : (sel?.hcoIdx ?? autoPick?.hcoIdx ?? -1);
        const selectedHCPId =
          hcpIdx >= 0 ? pd.hcps[hcpIdx]?.enterpriseId : undefined;
        const selectedHCOId =
          hcoIdx >= 0 ? pd.hcos[hcoIdx]?.enterpriseId : undefined;
        const dcrActionMessage =
          isAutoProcessed || (showCheckbox && checkedRecords?.has(record.id))
            ? getDcrActionMessage(
                record,
                { hcpIdx, hcoIdx, selectedHCPId, selectedHCOId },
                pd,
              )
            : "";
        const effectiveRecord = isAutoProcessed
          ? {
              ...record,
              hcpNotFound: record.hasHcp && !selectedHCPId,
              hcoNotFound: record.hasHco && !selectedHCOId,
            }
          : record;
        return (
          <RecordRow
            key={record.id}
            record={effectiveRecord}
            index={i}
            showStatus={showStatus}
            onStatusClick={onStatusClick}
            onRowClick={onRowClick}
            selectedHCPId={selectedHCPId}
            selectedHCOId={selectedHCOId}
            showCheckbox={showCheckbox}
            isChecked={checkedRecords?.has(record.id)}
            onToggleCheck={onToggleCheck}
            dcrActionMessage={dcrActionMessage}
            isAutoProcessed={isAutoProcessed}
            hcpAutoSelected={
              autoPick?.hcpIdx >= 0 && hcpIdx === autoPick.hcpIdx
            }
            hcoAutoSelected={
              autoPick?.hcoIdx >= 0 && hcoIdx === autoPick.hcoIdx
            }
          />
        );
      })}
    </div>
  );
}

export default function SearchResultProcess() {
  const history = useHistory();
  const UserInfo = useContext(UserContext);
  const userCountry = sessionStorage.getItem("userSelectedCountry");
  const isExpeditedFlow = !!UserInfo?.canExpeditedFlow;
  const [mainTab, setMainTab] = useState(isExpeditedFlow ? "auto" : "review");
  const [reviewSub, setReviewSub] = useState("eph");
  const [visitedReviewTabs, setVisitedReviewTabs] = useState(
    () => new Set(["eph"]),
  );
  const [selectAllAnchor, setSelectAllAnchor] = useState(null);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewSearchQuery, setReviewSearchQuery] = useState("");
  const [reviewStatusFilter, setReviewStatusFilter] = useState("all");
  const [modalRecord, setModalRecord] = useState(null);
  const [autoPage, setAutoPage] = useState(1);
  const [panelRecord, setPanelRecord] = useState(null);
  const [selections, setSelections] = useState({});
  const [checkedRecords, setCheckedRecords] = useState(new Set());
  const [showClearModal, setShowClearModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showSkipReviewModal, setShowSkipReviewModal] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [clearEPH, setClearEPH] = useState(true);
  const [clearVendor, setClearVendor] = useState(true);
  const [clearNotFound, setClearNotFound] = useState(true);
  const [filterRS, setFilterRS] = useState(new Set());
  const [filterSource, setFilterSource] = useState(new Set());
  const [subFilters, setSubFilters] = useState([]);
  const [subFilterCounter, setSubFilterCounter] = useState(0);
  const [filterOpen, setFilterOpen] = useState(true);
  const vendorAbortRef = useRef(null);
  const [reviewEphRecords, setReviewEphRecords] = useState([]);
  const [reviewVendorRecords, setReviewVendorRecords] = useState([]);
  const [reviewAutoRecords, setReviewAutoRecords] = useState([]);
  const hasAutoRecords = reviewAutoRecords.length > 0;
  const [reviewNotFoundRecords, setReviewNotFoundRecords] = useState([]);
  const [reviewCandidatesById, setReviewCandidatesById] = useState({});
  const [reviewVendorSearchStatusById, setReviewVendorSearchStatusById] =
    useState({});
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewLoaded, setReviewLoaded] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewCreatedAt, setReviewCreatedAt] = useState(null);
  const [isReviewExpired, setIsReviewExpired] = useState(false);
  const [autoProgressByPk, setAutoProgressByPk] = useState({});
  const [autoFetching, setAutoFetching] = useState(false);
  const [autoPolling, setAutoPolling] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const manualRefreshTimerRef = useRef(null);
  const [autoAllDone, setAutoAllDone] = useState(false);
  const [autoPollExpired, setAutoPollExpired] = useState(false);
  const autoMountedRef = useRef(true);
  const [autoSummary, setAutoSummary] = useState(null);

  useEffect(() => {
    const deadline = getReviewDeadline(reviewCreatedAt);
    if (!deadline) {
      setIsReviewExpired(false);
      return undefined;
    }

    const expireReview = () => {
      setIsReviewExpired(true);
      setShowClearModal(false);
      setShowSubmitModal(false);
      setShowSkipReviewModal(false);
    };

    const remainingMs = deadline.getTime() - Date.now();
    if (remainingMs <= 0) {
      expireReview();
      return undefined;
    }

    setIsReviewExpired(false);
    const timeoutId = setTimeout(expireReview, remainingMs);
    return () => clearTimeout(timeoutId);
  }, [reviewCreatedAt]);

  useEffect(() => {
    const uploadId = sessionStorage.getItem("uploadId");
    if (!uploadId) return undefined;

    let isCancelled = false;
    let fetchedRows = 0;
    const controller = new AbortController();
    const signal = controller.signal;
    const advanceSearchType = "advance";
    const startTime = Date.now();

    const sleep = (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

    const hasTimeLeft = () =>
      Date.now() - startTime < REVIEW_MAX_POLLING_TIME_MS;

    const fetchSummaryWithRetry = async (retryCount = 0) => {
      try {
        return await Api.fetchBulkSearchSummery({
          upload_id: uploadId,
          searchType: advanceSearchType,
          fetchFor: "BulkUpload",
        });
      } catch (error) {
        if (
          signal.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          throw error;
        }
        if (retryCount >= REVIEW_MAX_RETRIES || isCancelled) {
          throw error;
        }
        await sleep(REVIEW_RETRY_WAIT_TIME_MS);
        return fetchSummaryWithRetry(retryCount + 1);
      }
    };

    const fetchWithRetry = async (index, retryCount = 0) => {
      try {
        return await Api.fetchBulkSearchResults(
          {
            upload_id: uploadId,
            session: index === 0 ? "0" : "1",
            searchType: advanceSearchType,
            fetchFor: "BulkUpload",
          },
          signal,
        );
      } catch (error) {
        if (
          signal.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          throw error;
        }
        if (retryCount >= REVIEW_MAX_RETRIES || isCancelled) {
          throw error;
        }
        await sleep(REVIEW_RETRY_WAIT_TIME_MS);
        return fetchWithRetry(index, retryCount + 1);
      }
    };

    const fetchResultPage = async (index) => {
      const response = await fetchWithRetry(index);
      const responseData = response?.data;
      const newData = Array.isArray(responseData)
        ? responseData
        : responseData?.results || [];

      if (newData.length === 0) {
        return 0;
      }

      const {
        eph,
        vendor,
        auto,
        notFound,
        candidatesById,
        vendorStatusById,
        savedSelectionById,
      } = mapReviewApiResponse(newData, fetchedRows, isExpeditedFlow);
      fetchedRows += newData.length;

      if (eph.length > 0) {
        setReviewEphRecords((prev) => [...prev, ...eph]);
      }
      if (vendor.length > 0) {
        setReviewVendorRecords((prev) => [...prev, ...vendor]);
      }
      if (auto.length > 0) {
        setReviewAutoRecords((prev) => [...prev, ...auto]);
      }
      if (notFound.length > 0) {
        setReviewNotFoundRecords((prev) => [...prev, ...notFound]);
      }
      if (Object.keys(candidatesById).length > 0) {
        setReviewCandidatesById((prev) => ({ ...prev, ...candidatesById }));
      }
      if (!isExpeditedFlow) {
        const autoCheckedIds = [...eph, ...vendor]
          .filter((record) => {
            const pd = getPanelData(record, false, candidatesById);
            return pd.hcps.length === 1 || pd.hcos.length === 1;
          })
          .map((record) => record.id);
        if (autoCheckedIds.length > 0) {
          setCheckedRecords((prev) => new Set([...prev, ...autoCheckedIds]));
        }
      }
      const savedIds = Object.keys(savedSelectionById);
      if (savedIds.length > 0) {
        setSelections((prev) => ({ ...prev, ...savedSelectionById }));
        setCheckedRecords((prev) => new Set([...prev, ...savedIds]));
      }
      if (Object.keys(vendorStatusById).length > 0) {
        setReviewVendorSearchStatusById((prev) => {
          const next = { ...prev };
          Object.entries(vendorStatusById).forEach(([recordId, status]) => {
            next[recordId] = { ...status, ...next[recordId] };
          });
          return next;
        });
      }

      return newData.length;
    };

    const fetchReviewResults = async () => {
      let currentIndex = 0;
      let shouldContinue = true;

      setReviewLoading(true);
      setReviewError(null);
      setReviewEphRecords([]);
      setReviewVendorRecords([]);
      setReviewAutoRecords([]);
      setReviewNotFoundRecords([]);
      setReviewCandidatesById({});
      setReviewVendorSearchStatusById({});

      while (shouldContinue && !isCancelled) {
        if (!hasTimeLeft()) break;

        try {
          const summaryResponse = await fetchSummaryWithRetry();
          const summaryData = summaryResponse?.data || {};
          const createdAt = summaryData.created_at || summaryData.data?.created_at;
          if (createdAt) {
            setReviewCreatedAt(createdAt);
          }
          const searchStatus = String(summaryData?.search_status || "")
            .trim()
            .toUpperCase();

          if (searchStatus === "REVIEW_SUBMITTED") {
            shouldContinue = false;
            break;
          }

          const totalRecords = Number(summaryData?.total_records || 0);
          const recordsProcessed = Number(
            summaryData?.total_records_completed || 0,
          );

          if (recordsProcessed === 0) {
            if (!hasTimeLeft()) {
              shouldContinue = false;
              break;
            }
            await sleep(REVIEW_STATUS_API_POLL_INTERVAL_MS);
            continue;
          }

          const availableToFetch = Math.max(0, recordsProcessed - fetchedRows);

          if (availableToFetch > 0) {
            const pagesToFetch =
              availableToFetch > REVIEW_BULK_FETCH_BATCH_THRESHOLD
                ? REVIEW_BULK_FETCH_BATCH_PAGES
                : Math.min(
                    REVIEW_BULK_FETCH_BATCH_PAGES,
                    Math.ceil(availableToFetch / REVIEW_BULK_FETCH_PAGE_SIZE),
                  );

            for (
              let pageOffset = 0;
              pageOffset < pagesToFetch;
              pageOffset += 1
            ) {
              const fetchedCount = await fetchResultPage(currentIndex);
              currentIndex += 1;
              if (fetchedCount === 0 || isCancelled) break;
            }
          }

          const isProcessingComplete =
            totalRecords > 0 && recordsProcessed >= totalRecords;
          const hasFetchedAllProcessedRows = fetchedRows >= recordsProcessed;

          if (isProcessingComplete && hasFetchedAllProcessedRows) {
            shouldContinue = false;
            break;
          }

          if (!hasTimeLeft()) {
            shouldContinue = false;
            break;
          }

          await sleep(REVIEW_STATUS_API_POLL_INTERVAL_MS);
        } catch (error) {
          if (
            signal.aborted ||
            error?.name === "AbortError" ||
            error?.code === "ERR_CANCELED"
          ) {
          } else {
            setReviewError("Unable to load Review Required records.");
          }
          shouldContinue = false;
        }
      }

      setReviewLoading(false);
      if (!isCancelled) setReviewLoaded(true);
    };

    fetchReviewResults();

    return () => {
      isCancelled = true;
      controller.abort();
      setReviewLoading(false);
    };
  }, []);

  useEffect(() => {
    return () => {
      vendorAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    autoMountedRef.current = true;
    return () => {
      autoMountedRef.current = false;
    };
  }, []);

  const fetchAutoProgress = useCallback(async () => {
    const uploadId = sessionStorage.getItem("uploadId");
    if (!uploadId) return false;
    setAutoFetching(true);
    let allDone = false;
    try {
      const response = await Api.fetchAutoProcessResults({
        upload_id: uploadId,
      });
      if (!autoMountedRef.current) return false;
      const data = response?.data || {};
        const rawRecords = Array.isArray(data)
          ? data
          : data.records ||
            data.results ||
            data.data?.records ||
            data.data ||
            [];
        const records = Array.isArray(rawRecords) ? rawRecords : [];

        const byPk = {};
        records.forEach((rec) => {
          const pk = firstFilled(rec?.pk, rec?.Pk, rec?.PK);
          if (pk !== undefined) byPk[pk] = buildProgressDetail(rec);
        });
        setAutoProgressByPk(byPk);
        const summary = extractAutoSummary(data);
        if (summary) setAutoSummary(summary);

        allDone =
          records.length > 0 &&
          records.every((rec) =>
            ["processed", "failed"].includes(
              normalizeProgressStatus(rec?.record_status),
            ),
          );
        if (allDone) setAutoAllDone(true);
      } catch (error) {
        if (!autoMountedRef.current) return false;
        console.warn("fetchAutoProcessResults failed:", error);
      } finally {
        if (autoMountedRef.current) setAutoFetching(false);
      }
      return allDone;
    }, []);

  useEffect(() => {
    if (!isExpeditedFlow || !reviewLoaded || !hasAutoRecords) return undefined;

    let isCancelled = false;
    let timerId = null;
    const startTime = Date.now();
    setAutoPollExpired(false);
    setAutoPolling(true);

    const poll = async () => {
      const done = await fetchAutoProgress();
      if (isCancelled) return;
      if (done) {
        setAutoPolling(false);
        return;
      }
      if (Date.now() - startTime < AUTO_PROGRESS_POLL_WINDOW_MS) {
        timerId = setTimeout(poll, AUTO_PROGRESS_POLL_INTERVAL_MS);
      } else {
        setAutoPolling(false);
        setAutoPollExpired(true);
      }
    };

    poll();

    return () => {
      isCancelled = true;
      setAutoPolling(false);
      clearTimeout(timerId);
    };
  }, [isExpeditedFlow, reviewLoaded, hasAutoRecords, fetchAutoProgress]);

  const handleManualAutoRefresh = async () => {
    if (manualRefreshing || autoAllDone || !hasAutoRecords) return;
    setManualRefreshing(true);
    const startTime = Date.now();
    const run = async () => {
      const done = await fetchAutoProgress();
      if (!autoMountedRef.current) return;
      if (
        done ||
        Date.now() - startTime + AUTO_PROGRESS_POLL_INTERVAL_MS >=
          AUTO_MANUAL_REFRESH_WINDOW_MS
      ) {
        setManualRefreshing(false);
        return;
      }
      manualRefreshTimerRef.current = setTimeout(
        run,
        AUTO_PROGRESS_POLL_INTERVAL_MS,
      );
    };
    run();
  };

  useEffect(
    () => () => clearTimeout(manualRefreshTimerRef.current),
    [],
  );

  const normalizeCommercialCountries = () => {
    const value =
      UserInfo?.commercialCountry ??
      UserInfo?.commercialCountries ??
      UserInfo?.countries ??
      [];
    return Array.isArray(value) ? value : [value].filter(Boolean);
  };

  const findReviewRecordById = (recordId) =>
    reviewEphRecords.find((r) => r.id === recordId) ||
    reviewVendorRecords.find((r) => r.id === recordId);

  const patchReviewVendorStatus = (recordId, patch) =>
    setReviewVendorSearchStatusById((prev) => ({
      ...prev,
      [recordId]: { ...(prev[recordId] || {}), ...patch },
    }));

  const handleReviewVendorSearch = async (recordId, entityType) => {
    const record = findReviewRecordById(recordId);
    const uploadId = sessionStorage.getItem("uploadId");
    if (!record?.pk || !uploadId || !entityType) return;

    vendorAbortRef.current?.abort();
    const controller = new AbortController();
    vendorAbortRef.current = controller;

    patchReviewVendorStatus(recordId, { [entityType]: "SUBMITTED_IN_QUEUE" });

    const payload = {
      upload_id: uploadId,
      eConsentCountry: userCountry,
      commercialCountries: normalizeCommercialCountries(),
      pk: record.pk,
      ...(entityType === "hcp" ? { hcp_search_type: "hcp" } : {}),
      ...(entityType === "hco" ? { hco_search_type: "hco" } : {}),
      ...(entityType === "parent_hco"
        ? { parent_hco_search_type: "parent_hco" }
        : {}),
    };

    try {
      const triggerResponse = await Api.fetchVendorSearch(payload);
      if (controller.signal.aborted) return;
      if (triggerResponse?.status !== 200) {
        patchReviewVendorStatus(recordId, { [entityType]: "FAILED" });
        return;
      }

      patchReviewVendorStatus(recordId, { [entityType]: "IN_PROGRESS" });

      const pollingSearchType = entityType;
      const maxPollingTime = 50 * 60 * 1000;
      const pollingWaitTime = 5000;
      const startTime = Date.now();
      let shouldContinuePolling = true;

      while (shouldContinuePolling) {
        if (controller.signal.aborted) break;
        if (Date.now() - startTime >= maxPollingTime) break;
        await new Promise((resolve) => setTimeout(resolve, pollingWaitTime));
        if (controller.signal.aborted) break;

        try {
          const pollingResponse = await Api.fetchVendorSearchPolling({
            upload_id: uploadId,
            pk: record.pk,
            search_type: pollingSearchType,
          });
          if (controller.signal.aborted) break;
          const responseData = pollingResponse?.data || {};
          shouldContinuePolling = responseData?.continuePolling === true;

          const statusKey =
            entityType === "hcp"
              ? "hcp_vendor_search_status"
              : entityType === "hco"
                ? "hco_vendor_search_status"
                : "parent_hco_vendor_search_status";
          if (responseData?.[statusKey]) {
            patchReviewVendorStatus(recordId, {
              [entityType]: responseData[statusKey],
            });
          }

          const searchResultsPayload =
            responseData?.search_results || responseData?.search?.results;
          const vendorMatches =
            searchResultsPayload?.vendor?.[entityType] || null;
          if (vendorMatches) {
            const matchesArray = Array.isArray(vendorMatches)
              ? vendorMatches
              : vendorMatches.matches || vendorMatches.results || [];
            const mapped = matchesArray.map((item) =>
              normalizeReviewEntityFields(
                item,
                "vendor",
                entityType === "hcp" ? "hcp" : "hco",
              ),
            );

            setReviewCandidatesById((prev) => {
              const existing = prev[recordId] || {
                eph: { hcps: [], hcos: [] },
                vendor: { hcps: [], hcos: [] },
              };
              const vendorBucket = existing.vendor || { hcps: [], hcos: [] };
              return {
                ...prev,
                [recordId]: {
                  ...existing,
                  vendor:
                    entityType === "hcp"
                      ? {
                          ...vendorBucket,
                          hcps: [...vendorBucket.hcps, ...mapped],
                        }
                      : {
                          ...vendorBucket,
                          hcos: [...vendorBucket.hcos, ...mapped],
                        },
                },
              };
            });
          }

          if (!shouldContinuePolling) break;
        } catch (pollingError) {
          if (
            controller.signal.aborted ||
            pollingError?.name === "AbortError" ||
            pollingError?.code === "ERR_CANCELED"
          ) {
            break;
          }
          console.warn("Vendor search polling error:", pollingError);
        }
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        patchReviewVendorStatus(recordId, { [entityType]: "FAILED" });
      }
    } finally {
      if (!controller.signal.aborted) {
        setReviewVendorSearchStatusById((prev) => {
          const current = prev?.[recordId]?.[entityType];
          if (current === "IN_PROGRESS") {
            return {
              ...prev,
              [recordId]: { ...prev[recordId], [entityType]: "COMPLETED" },
            };
          }
          return prev;
        });
      }
    }
  };

  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [submitReviewError, setSubmitReviewError] = useState(null);
  const [isSavingReview, setIsSavingReview] = useState(false);
  const [saveToast, setSaveToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    if (!saveToast.open) return undefined;
    const t = setTimeout(
      () => setSaveToast((prev) => ({ ...prev, open: false })),
      4000,
    );
    return () => clearTimeout(t);
  }, [saveToast]);

  const getPayloadByteSize = (payload) =>
    new TextEncoder().encode(JSON.stringify(payload || {})).length;

  const splitPayloadIntoBatches = (payload, maxBytes) => {
    const records = Array.isArray(payload?.records) ? payload.records : [];
    const basePayload = { ...payload, records: [] };
    const baseSize = getPayloadByteSize(basePayload);
    const batches = [];
    let currentBatch = [];
    let currentSize = baseSize;

    for (const record of records) {
      const recordSize = getPayloadByteSize(record);
      if (currentSize + recordSize > maxBytes && currentBatch.length > 0) {
        batches.push({ ...basePayload, records: currentBatch });
        currentBatch = [record];
        currentSize = baseSize + recordSize;
      } else {
        currentBatch.push(record);
        currentSize += recordSize;
      }
    }

    if (currentBatch.length > 0) {
      batches.push({ ...basePayload, records: currentBatch });
    }

    return batches;
  };

  const buildReviewSubmitPayload = () => {
    const uploadId = sessionStorage.getItem("uploadId");
    const checkedList = [
      ...reviewEphRecords,
      ...reviewVendorRecords,
      ...reviewNotFoundRecords,
    ].filter((r) => checkedRecords.has(r.id));

    const records = checkedList.map((record) => {
      if (record.isNotFoundRecord) {
        const notFoundPayload = {
          status: "REVIEWED",
          pk: record.pk || "",
          selected_result: false,
          is_flagged_as_not_found: true,
        };
        const leftKey = record.leftEntityType === "hco" ? "hco" : "hcp";
        const rightKey =
          record.rightEntityType === "parent_hco" ? "parent_hco" : "hco";
        if (record.hasHcp) {
          notFoundPayload[`is_${leftKey}_selected`] = false;
          notFoundPayload[`${leftKey}_selected_source`] = "";
        }
        if (record.hasHco) {
          notFoundPayload[`is_${rightKey}_selected`] = false;
          notFoundPayload[`${rightKey}_selected_source`] = "";
        }
        return notFoundPayload;
      }

      const pd = getPanelData(record, false, reviewCandidatesById);
      const sel = getSelection(record.id, false);
      const selectedHcp = sel.hcpIdx >= 0 ? pd.hcps[sel.hcpIdx] : null;
      const selectedHco = sel.hcoIdx >= 0 ? pd.hcos[sel.hcoIdx] : null;
      const leftKey = record.leftEntityType === "hco" ? "hco" : "hcp";
      const rightKey =
        record.rightEntityType === "parent_hco" ? "parent_hco" : "hco";

      const selectedResult = {};
      if (selectedHcp?.raw) selectedResult[leftKey] = selectedHcp.raw;
      if (selectedHco?.raw) selectedResult[rightKey] = selectedHco.raw;

      const payload = {
        status: "REVIEWED",
        pk: record.pk || "",
        selected_result:
          Object.keys(selectedResult).length > 0 ? selectedResult : false,
      };

      if (record.type === "hcp_hco" || record.type === "hcp_only") {
        if (leftKey === "hco") {
          payload.is_hco_selected = !!selectedHcp;
          payload.hco_selected_source = selectedHcp?.sourceType || "";
        } else {
          payload.is_hcp_selected = !!selectedHcp;
          payload.hcp_selected_source = selectedHcp?.sourceType || "";
        }
      }

      if (record.type === "hcp_hco" || record.type === "hco_only") {
        if (rightKey === "hco") {
          payload.is_hco_selected = !!selectedHco;
          payload.hco_selected_source = selectedHco?.sourceType || "";
        } else {
          payload.is_parent_hco_selected = !!selectedHco;
          payload.parent_hco_selected_source = selectedHco?.sourceType || "";
        }
      }

      return payload;
    });

    return { upload_id: uploadId || "", records };
  };

  const sendReviewBatches = async (payload) => {
    const MAX_PAYLOAD_BYTES = 4 * 1024 * 1024;
    const totalSize = getPayloadByteSize(payload);
    if (totalSize <= MAX_PAYLOAD_BYTES) {
      await Api.batchSearchResult(payload);
    } else {
      const batches = splitPayloadIntoBatches(payload, MAX_PAYLOAD_BYTES);
      for (const batch of batches) {
        await Api.batchSearchResult(batch);
      }
    }
  };

  const submitReviewPayload = async (payload) => {
    await sendReviewBatches(payload);
    await new Promise((resolve) => setTimeout(resolve, 5000));
    await Api.triggerBatchResult({ upload_id: payload.upload_id });
  };

  const handleSaveReview = async () => {
    if (isReviewExpired) return;
    setIsSavingReview(true);
    try {
      await sendReviewBatches({
        ...buildReviewSubmitPayload(),
      });
      setSaveToast({
        open: true,
        severity: "success",
        message: "Review saved successfully",
      });
    } catch (error) {
      setSaveToast({
        open: true,
        severity: "error",
        message: "Failed to save review. Please try again.",
      });
    } finally {
      setIsSavingReview(false);
    }
  };

  const handleConfirmSubmitReview = async () => {
    if (isReviewExpired) return;
    setIsSubmittingReview(true);
    setSubmitReviewError(null);
    try {
      const payload = buildReviewSubmitPayload();
      await submitReviewPayload(payload);
      setShowSubmitModal(false);
      history.push("/bulk-upload/results");
    } catch (error) {
      setSubmitReviewError("Failed to submit review. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const escapeCsvValue = (value) => {
    const normalizedValue = value == null ? "" : String(value);
    if (
      normalizedValue.includes(",") ||
      normalizedValue.includes('"') ||
      normalizedValue.includes("\n")
    ) {
      return `"${normalizedValue.replace(/"/g, '""')}"`;
    }
    return normalizedValue;
  };

  const toTitleCaseHeader = (value) =>
    String(value || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const triggerCsvDownload = (headers, rows, fileName) => {
    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF", csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getFirstValue = (...values) =>
    values.find(
      (value) => value !== undefined && value !== null && value !== "",
    );

  const formatExportCell = (rawValue) => {
    if (rawValue === undefined || rawValue === null) return "";
    if (typeof rawValue === "object") {
      try {
        return JSON.stringify(rawValue);
      } catch (error) {
        return String(rawValue);
      }
    }
    return rawValue;
  };

  const getExportCandidates = (record, entityKey) => {
    const pd = getPanelData(record, false, reviewCandidatesById);
    if (record.leftEntityType === entityKey) return pd.hcps;
    if (entityKey === "hco" && record.rightEntityType === "hco") return pd.hcos;
    if (entityKey === "parentHco" && record.rightEntityType === "parent_hco") {
      return pd.hcos;
    }
    return [];
  };

  const getExportResultObject = (candidate) => {
    const raw =
      candidate?.raw && typeof candidate.raw === "object" ? candidate.raw : {};
    return {
      ...raw,
      name: candidate?.name,
      address: candidate?.address,
      classificationCode: candidate?.classificationCode,
      entityId: getFirstValue(raw?.EntityId, candidate?.enterpriseId, "") || "",
      activeFlag: getFirstValue(raw?.ActiveFlag, "") || "",
    };
  };

  const buildReviewEntityExportDataset = (records, entityKey) => {
    const excludedResultHeaders = new Set([
      "enterprise id",
      "country",
      "status",
      "primary specialty",
      "source type",
      "phone",
      "phone number",
    ]);
    const hcoOnlyExcludedResultHeaders =
      entityKey === "hco" ? new Set(["first name", "last name"]) : new Set();

    const validEntries = records
      .map((record) => {
        const input = record?.inputEntities?.[entityKey];
        if (
          !input ||
          typeof input !== "object" ||
          Object.keys(input).length === 0
        ) {
          return null;
        }
        return {
          input,
          resultItems: getExportCandidates(record, entityKey),
        };
      })
      .filter(Boolean);

    const inputFieldKeys = [];
    const inputFieldKeySet = new Set();
    validEntries.forEach(({ input }) => {
      Object.keys(input).forEach((key) => {
        if (!inputFieldKeySet.has(key)) {
          inputFieldKeySet.add(key);
          inputFieldKeys.push(key);
        }
      });
    });

    const resultFieldKeys = [];
    const resultFieldKeySet = new Set();
    validEntries.forEach(({ resultItems }) => {
      resultItems.forEach((item) => {
        Object.keys(getExportResultObject(item)).forEach((key) => {
          if (!resultFieldKeySet.has(key)) {
            resultFieldKeySet.add(key);
            resultFieldKeys.push(key);
          }
        });
      });
    });

    const inputColumnsToKeep = inputFieldKeys.filter((key) =>
      validEntries.some(({ input }) => {
        const value = input?.[key];
        return (
          value !== undefined && value !== null && String(value).trim() !== ""
        );
      }),
    );

    const orderedResultColumns = resultFieldKeys.filter((key) => {
      const header = toTitleCaseHeader(key).toLowerCase();
      return (
        !excludedResultHeaders.has(header) &&
        !hcoOnlyExcludedResultHeaders.has(header)
      );
    });
    const firstNameIndex = orderedResultColumns.findIndex(
      (key) => toTitleCaseHeader(key).toLowerCase() === "first name",
    );
    const middleNameIndex = orderedResultColumns.findIndex(
      (key) => toTitleCaseHeader(key).toLowerCase() === "middle name",
    );
    if (firstNameIndex !== -1 && middleNameIndex !== -1) {
      const [middleNameKey] = orderedResultColumns.splice(middleNameIndex, 1);
      const adjustedFirstNameIndex = orderedResultColumns.findIndex(
        (key) => toTitleCaseHeader(key).toLowerCase() === "first name",
      );
      orderedResultColumns.splice(adjustedFirstNameIndex + 1, 0, middleNameKey);
    }

    const uniqueInputColumns = [];
    const seenInputHeaders = new Set();
    inputColumnsToKeep.forEach((key) => {
      const header = `Input ${toTitleCaseHeader(key)}`;
      if (!seenInputHeaders.has(header.toLowerCase())) {
        seenInputHeaders.add(header.toLowerCase());
        uniqueInputColumns.push({ key, header });
      }
    });

    const uniqueResultColumns = [];
    const seenResultHeaders = new Set();
    orderedResultColumns.forEach((key) => {
      const header = toTitleCaseHeader(key);
      if (!seenResultHeaders.has(header.toLowerCase())) {
        seenResultHeaders.add(header.toLowerCase());
        uniqueResultColumns.push({ key, header });
      }
    });

    const rows = validEntries.flatMap(({ input, resultItems }) => {
      const uniqueResultObjects = [];
      const uniqueResultSet = new Set();
      resultItems.forEach((item) => {
        const exportResult = getExportResultObject(item);
        const dedupeKey = JSON.stringify(exportResult);
        if (!uniqueResultSet.has(dedupeKey)) {
          uniqueResultSet.add(dedupeKey);
          uniqueResultObjects.push(exportResult);
        }
      });
      return uniqueResultObjects.map((exportResult) => [
        ...uniqueInputColumns.map((column) =>
          getFirstValue(input?.[column.key], ""),
        ),
        ...uniqueResultColumns.map((column) =>
          formatExportCell(exportResult?.[column.key]),
        ),
      ]);
    });

    const uniqueRows = [];
    const uniqueRowSet = new Set();
    rows.forEach((row) => {
      const dedupeKey = JSON.stringify(row);
      if (!uniqueRowSet.has(dedupeKey)) {
        uniqueRowSet.add(dedupeKey);
        uniqueRows.push(row);
      }
    });

    return {
      headers: [
        "Row",
        ...uniqueInputColumns.map((column) => column.header),
        ...uniqueResultColumns.map((column) => column.header),
      ],
      rows: uniqueRows.map((row, index) => [index + 1, ...row]),
    };
  };

  const buildNotFoundEntityInputExportDataset = (
    records,
    entityKey,
    entityLabel,
  ) => {
    const validRecords = records.filter((record) => {
      const entityInput = record?.inputEntities?.[entityKey];
      return (
        entityInput &&
        typeof entityInput === "object" &&
        Object.keys(entityInput).length > 0
      );
    });

    const inputFieldKeys = [];
    const inputFieldKeySet = new Set();
    validRecords.forEach((record) => {
      Object.keys(record.inputEntities[entityKey]).forEach((fieldKey) => {
        if (!inputFieldKeySet.has(fieldKey)) {
          inputFieldKeySet.add(fieldKey);
          inputFieldKeys.push(fieldKey);
        }
      });
    });

    const columnsToKeep = inputFieldKeys.filter((fieldKey) =>
      validRecords.some((record) => {
        const value = record.inputEntities[entityKey][fieldKey];
        return (
          value !== undefined && value !== null && String(value).trim() !== ""
        );
      }),
    );

    return {
      headers: [
        "Row",
        ...columnsToKeep.map(
          (fieldKey) => `Input ${entityLabel} ${toTitleCaseHeader(fieldKey)}`,
        ),
      ],
      rows: validRecords.map((record, index) => [
        index + 1,
        ...columnsToKeep.map((fieldKey) =>
          formatExportCell(record.inputEntities[entityKey][fieldKey]),
        ),
      ]),
    };
  };

  const handleExportReview = () => {
    if (mainTab !== "review") return;

    const entityExports = [
      { key: "hcp", label: "HCP", filePart: "hcp" },
      { key: "hco", label: "HCO", filePart: "hco" },
      { key: "parentHco", label: "Parent HCO", filePart: "parent_hco" },
    ];

    if (reviewSub === "notfound") {
      entityExports.forEach(({ key, label, filePart }) => {
        const dataset = buildNotFoundEntityInputExportDataset(
          reviewNotFoundRecords,
          key,
          label,
        );
        if (dataset.rows.length > 0) {
          triggerCsvDownload(
            dataset.headers,
            dataset.rows,
            `not_found_${filePart}_input_results.csv`,
          );
        }
      });
      return;
    }

    const records =
      reviewSub === "eph" ? reviewEphRecords : reviewVendorRecords;
    if (records.length === 0) return;

    const tabPrefix = reviewSub === "eph" ? "eph" : "vendor";
    const tabLabel = reviewSub === "eph" ? "EPH" : "Vendor";

    const filesToDownload = entityExports
      .map(({ key, filePart }) => ({
        dataset: buildReviewEntityExportDataset(records, key),
        fileName: `${tabPrefix}_${filePart}_input_results.csv`,
      }))
      .filter(
        ({ dataset }) =>
          Array.isArray(dataset?.rows) && dataset.rows.length > 0,
      );

    if (filesToDownload.length === 0) {
      const fallbackHeaders = [
        "Tab",
        "Name",
        "Classification Code",
        "Active Flag",
        "Address",
        "EntityId",
      ];
      const fallbackRows = records.flatMap((record) => {
        const pd = getPanelData(record, false, reviewCandidatesById);
        return [...pd.hcps, ...pd.hcos].map((candidate) => [
          tabLabel,
          candidate.name,
          candidate.classificationCode,
          getFirstValue(candidate.raw?.ActiveFlag, "") || "",
          candidate.address,
          getFirstValue(candidate.raw?.EntityId, candidate.enterpriseId, ""),
        ]);
      });
      triggerCsvDownload(
        fallbackHeaders,
        fallbackRows,
        `${tabPrefix}_results.csv`,
      );
      return;
    }

    filesToDownload.forEach(({ dataset, fileName }) => {
      triggerCsvDownload(dataset.headers, dataset.rows, fileName);
    });
  };

  const handleExportAuto = async () => {
    if (mainTab !== "auto" || combinedAutoRecords.length === 0) return;

    const statusLabel = (s) =>
      ({
        pending: "Pending",
        in_progress: "In Progress",
        processed: "Processed",
        completed: "Completed",
        failed: "Failed",
        partially_processed: "Partially Processed",
      })[s] ||
      (s ? String(s) : "N/A");
    const detailText = (part) => {
      const lines = (part?.rows || []).map((r) => `${r.label}: ${r.value}`);
      if (part?.error) lines.push(`Error: ${part.error}`);
      return lines.join("\n");
    };
    const inputText = (record) =>
      Object.entries(record.inputEntities || {})
        .filter(([, v]) => v && typeof v === "object")
        .map(([entity, fields]) =>
          [
            entity.toUpperCase(),
            ...Object.entries(fields).map(
              ([k, v]) => `${k}: ${formatExportCell(v)}`,
            ),
          ].join("\n"),
        )
        .join("\n\n");

    const headers = [
      "Row",
      "Input",
      "HCP Name",
      "HCO Name",
      "Selected HCP Enterprise ID",
      "Selected HCO Enterprise ID",
      "Result Status",
      "DCR Status",
      "DCR Details",
      "Subscription Status",
      "Subscription Details",
      "Consent Status",
      "Consent Details",
    ];

    const headerRow = headers.map((header) => ({
      value: header,
      fontWeight: "bold",
      color: "#FFFFFF",
      backgroundColor: "#CC1F1F",
      alignVertical: "center",
      align: "left",
      wrap: true,
      height: 24,
      borderColor: "#E5E7EB",
      borderStyle: "thin",
    }));
    const dataRows = combinedAutoRecords.map((record, index) => {
      const d = record.resultDetail;
      const pd = getPanelData(record, true, reviewCandidatesById);
      const sel = record.autoSelection || { hcpIdx: -1, hcoIdx: -1 };
      const selHcp = sel.hcpIdx >= 0 ? pd.hcps[sel.hcpIdx] : null;
      const selHco = sel.hcoIdx >= 0 ? pd.hcos[sel.hcoIdx] : null;
      const values = [
        record.inputRowNumber ?? index + 1,
        inputText(record),
        record.hcp?.name || "N/A",
        record.hco?.name || "N/A",
        selHcp?.enterpriseId || "N/A",
        selHco?.enterpriseId || "N/A",
        d ? statusLabel(deriveResultStatus(d)) : "N/A",
        statusLabel(d?.dcr?.status),
        detailText(d?.dcr),
        statusLabel(d?.subscription?.status),
        detailText(d?.subscription),
        statusLabel(d?.consent?.status),
        detailText(d?.consent),
      ];
      return values.map((value) => ({
        value: value ?? "",
        alignVertical: "top",
        align: "left",
        wrap: true,
        height: 64,
        borderColor: "#E5E7EB",
        borderStyle: "thin",
      }));
    });

    const columns = [10, 42, 25, 25, 24, 24, 16, 16, 40, 18, 44, 16, 40].map(
      (width) => ({ width }),
    );

    const data = await writeExcelFile([headerRow, ...dataRows], {
      sheet: "Auto Processed",
      columns,
      stickyRowsCount: 1,
      autoFilter: true,
    }).toBlob();
    FileSaver.saveAs(
      data,
      `${sessionStorage.getItem("uploadId")}_auto_processed_export.xlsx`,
    );
  };

  const addSubFilter = () => {
    setSubFilters((f) => {
      const usedFields = new Set(f.map((x) => x.field));
      const nextField =
        STATUS_CONDITION_FIELDS.find((field) => !usedFields.has(field)) ||
        STATUS_CONDITION_FIELDS[0];
      return [...f, { id: subFilterCounter, field: nextField, value: "" }];
    });
    setSubFilterCounter((c) => c + 1);
  };
  const removeSubFilter = (id) =>
    setSubFilters((f) => f.filter((x) => x.id !== id));
  const updateSubFilter = (id, patch) =>
    setSubFilters((f) => f.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const activeSubs = subFilters.filter((f) => f.value !== "");
  const hasFilters =
    filterRS.size > 0 || filterSource.size > 0 || activeSubs.length > 0;

  const combinedAutoRecords = useMemo(
    () =>
      reviewAutoRecords.map((r) =>
        r.pk != null && autoProgressByPk[r.pk]
          ? { ...r, resultDetail: autoProgressByPk[r.pk] }
          : r,
      ),
    [reviewAutoRecords, autoProgressByPk],
  );

  const filteredAuto = useMemo(
    () =>
      combinedAutoRecords.filter((r) => {
        const d = r.resultDetail;
        if (filterRS.size > 0) {
          const rs = d ? deriveResultStatus(d) : "processed";
          if (!filterRS.has(rs)) return false;
        }
        if (filterSource.size > 0 && !filterSource.has(r.sourceType)) {
          return false;
        }
        for (const sf of activeSubs) {
          if (!d) return false;
          const actual =
            sf.field === "dcr"
              ? d.dcr.status
              : sf.field === "subscription"
                ? d.subscription.status
                : d.consent.status;
          const matchesStatus =
            sf.value === "applicable"
              ? ["pending", "in_progress", "completed", "failed"].includes(
                  actual,
                )
              : actual === sf.value;
          if (!matchesStatus) return false;
        }
        return true;
      }),
    [combinedAutoRecords, filterRS, filterSource, activeSubs],
  );

  const pagedAuto = filteredAuto.slice(
    (autoPage - 1) * PAGE_SIZE,
    autoPage * PAGE_SIZE,
  );
  const counts = useMemo(() => {
    const result = {
      total: filteredAuto.length,
      pending: 0,
      in_progress: 0,
      processed: 0,
      failed: 0,
    };
    let unpolled = 0;
    filteredAuto.forEach((r) => {
      if (r.resultDetail?.recordStatus) {
        result[deriveResultStatus(r.resultDetail)]++;
      } else {
        unpolled++;
      }
    });
    if (!autoSummary) result.pending += unpolled;
    if (autoSummary) {
      result.total = autoSummary.total ?? result.total;
      result.processed = autoSummary.processed ?? result.processed;
      result.failed = autoSummary.failed ?? result.failed;
      result.in_progress = autoSummary.in_progress ?? result.in_progress;
      result.pending = autoSummary.pending ?? result.pending;
    }
    return result;
  }, [filteredAuto, autoSummary]);

  const getReviewDefaultSelection = (id) => {
    const none = { hcpIdx: -1, hcoIdx: -1 };
    if (isExpeditedFlow) return none;
    const record = [...reviewEphRecords, ...reviewVendorRecords].find(
      (r) => r.id === id,
    );
    return record ? getReviewAutoSelection(record) : none;
  };
  const getSelection = (id, auto = false) =>
    selections[id] ??
    (auto
      ? (combinedAutoRecords.find((r) => r.id === id)?.autoSelection ?? {
          hcpIdx: -1,
          hcoIdx: -1,
        })
      : getReviewDefaultSelection(id));
  const setSelection = (id, s) => {
    setSelections((prev) => ({ ...prev, [id]: s }));
    if (mainTab === "review") {
      const hasAnySelection = s.hcpIdx >= 0 || s.hcoIdx >= 0;
      setCheckedRecords((prev) => {
        const next = new Set(prev);
        hasAnySelection ? next.add(id) : next.delete(id);
        return next;
      });
    }
  };

  const openPanel = (r) => setPanelRecord(r);

  const closePanel = () => {
    setPanelRecord(null);
  };

  const toggleCheck = (id) =>
    setCheckedRecords((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setSelections((prevSel) => {
          if (!(id in prevSel)) {
            return prevSel;
          }
          const nextSel = { ...prevSel };
          delete nextSel[id];
          return nextSel;
        });
      } else {
        next.add(id);
      }
      return next;
    });

  const anyChecked =
    reviewEphRecords.some((r) => checkedRecords.has(r.id)) ||
    reviewVendorRecords.some((r) => checkedRecords.has(r.id)) ||
    reviewNotFoundRecords.some((r) => checkedRecords.has(r.id));
  const totalChecked = [
    ...reviewEphRecords,
    ...reviewVendorRecords,
    ...reviewNotFoundRecords,
  ].filter((r) => checkedRecords.has(r.id)).length;

  const filterReviewRecordsBySearch = (records) => {
    const query = reviewSearchQuery.trim().toLowerCase();
    if (!query) {
      return records;
    }
    const queryTokens = query.split(/\s+/).filter(Boolean);
    return records.filter((record) => {
      const hcpInput = record.inputEntities?.hcp || {};
      const hcpRaw = record.hcp?.raw || {};
      const vendorIndividual = hcpRaw.individual || {};
      const vendorEntity = hcpRaw.entity || {};
      const firstName =
        hcpInput.first_name ||
        hcpRaw.FirstName ||
        vendorIndividual.firstName ||
        vendorEntity.first_name__v ||
        "";
      const lastName =
        hcpInput.last_name ||
        hcpRaw.LastName ||
        vendorIndividual.lastName ||
        vendorEntity.last_name__v ||
        "";
      const fields = [
        record.hcp?.enterpriseId,
        record.hcp?.name,
        firstName,
        lastName,
        `${firstName} ${lastName}`.trim(),
        record.hcp?.city,
        record.hcp?.licenseId,
        record.hcp?.classificationCode,
        record.hcp?.primarySpeciality,
        record.hco?.enterpriseId,
        record.hco?.name,
        record.hco?.city,
        record.hco?.classificationCode,
        record.hco?.primarySpeciality,
      ];
      const searchableFields = fields.map((value) =>
        String(value || "").toLowerCase(),
      );
      return queryTokens.every((token) =>
        searchableFields.some((field) => field.includes(token)),
      );
    });
  };

  const getReviewAutoSelection = (record) => {
    const pd = getPanelData(record, false, reviewCandidatesById);
    return {
      hcpIdx: pd.hcps.length === 1 ? 0 : -1,
      hcoIdx: pd.hcos.length === 1 ? 0 : -1,
    };
  };

  const countCheckedAutoSelected = (records) =>
    isExpeditedFlow
      ? 0
      : records.filter((r) => {
          if (!checkedRecords.has(r.id)) return false;
          const auto = getReviewAutoSelection(r);
          return auto.hcpIdx >= 0 || auto.hcoIdx >= 0;
        }).length;

  const reviewSubSearchRecords = filterReviewRecordsBySearch(
    reviewSub === "eph"
      ? reviewEphRecords
      : reviewSub === "vendor"
        ? reviewVendorRecords
        : reviewNotFoundRecords,
  );
  const currentReviewSubRecords = reviewSubSearchRecords.filter((r) => {
    if (reviewStatusFilter === "reviewed") return checkedRecords.has(r.id);
    if (reviewStatusFilter === "pending") return !checkedRecords.has(r.id);
    return true;
  });
  const selectedCountInReviewSub = currentReviewSubRecords.filter((r) =>
    checkedRecords.has(r.id),
  ).length;
  const isReviewHeaderCheckboxChecked =
    currentReviewSubRecords.length > 0 &&
    selectedCountInReviewSub === currentReviewSubRecords.length;
  const isReviewHeaderCheckboxIndeterminate =
    !isReviewHeaderCheckboxChecked && selectedCountInReviewSub > 0;
  const isReviewHeaderCheckboxDisabled = currentReviewSubRecords.length === 0;

  const handleSelectAllRecords = () => {
    const nextSelections = { ...selections };
    setCheckedRecords((prev) => {
      const next = new Set(prev);
      currentReviewSubRecords.forEach((record) => {
        const auto = getReviewAutoSelection(record);
        if (auto.hcpIdx >= 0 || auto.hcoIdx >= 0) {
          nextSelections[record.id] = auto;
        }
        next.add(record.id);
      });
      return next;
    });
    setSelections(nextSelections);
  };

  const handleSelectAllAutoSelected = () => {
    const nextSelections = { ...selections };
    const autoIds = [];
    currentReviewSubRecords.forEach((record) => {
      const auto = getReviewAutoSelection(record);
      if (auto.hcpIdx >= 0 || auto.hcoIdx >= 0) {
        nextSelections[record.id] = auto;
        autoIds.push(record.id);
      }
    });
    setSelections(nextSelections);
    setCheckedRecords((prev) => new Set([...prev, ...autoIds]));
    setSelectAllAnchor(null);
  };

  const handleClearReviewSubSelection = () => {
    setCheckedRecords((prev) => {
      const next = new Set(prev);
      currentReviewSubRecords.forEach((record) => next.delete(record.id));
      return next;
    });
    setSelections((prev) => {
      const next = { ...prev };
      currentReviewSubRecords.forEach((record) => delete next[record.id]);
      return next;
    });
  };

  const allReviewRecords = [
    ...reviewEphRecords,
    ...reviewVendorRecords,
    ...reviewNotFoundRecords,
  ];
  const isEveryReviewRecordSelected =
    allReviewRecords.length > 0 &&
    allReviewRecords.every((r) => checkedRecords.has(r.id));

  const handleConfirmSkipReviewSelectAll = () => {
    const nextSelections = { ...selections };
    allReviewRecords.forEach((record) => {
      const pd = getPanelData(record, false, reviewCandidatesById);
      const hcpIdx = pd.hcps.length === 1 ? 0 : -1;
      const hcoIdx = pd.hcos.length === 1 ? 0 : -1;
      if (hcpIdx >= 0 || hcoIdx >= 0) {
        nextSelections[record.id] = { hcpIdx, hcoIdx };
      }
    });
    setSelections(nextSelections);
    setCheckedRecords((prev) => {
      const next = new Set(prev);
      allReviewRecords.forEach((record) => next.add(record.id));
      return next;
    });
    setShowSkipReviewModal(false);
    setVisitedReviewTabs(new Set(reviewSubTabs.map((tab) => tab.k)));
    setShowSubmitModal(true);
  };

  // Not Found is only offered when the expedited flow is off.
  const reviewSubTabs = [
    { k: "eph", lbl: "EPH", cnt: reviewEphRecords.length },
    { k: "vendor", lbl: "Vendor", cnt: reviewVendorRecords.length },
    ...(isExpeditedFlow
      ? []
      : [
          {
            k: "notfound",
            lbl: "Not Found",
            cnt: reviewNotFoundRecords.length,
          },
        ]),
  ];
  // Tabs without records have nothing to review, so they are not required.
  const allReviewTabsVisited = reviewSubTabs
    .filter((tab) => tab.cnt > 0)
    .every((tab) => visitedReviewTabs.has(tab.k));
  const isSubmitReviewEnabled =
    !isReviewExpired &&
    anyChecked &&
    allReviewTabsVisited &&
    !(!isExpeditedFlow && reviewLoading);

  const reviewDeadline = getReviewDeadline(reviewCreatedAt);
  const deadline = reviewDeadline
    ? reviewDeadline.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "unavailable";

  return (
    <div className="container-fluid body-height extra-margin-top-less mb-4">
      <Card sx={{ maxWidth: "100%" }} className="mx-auto">
        <div className={`rc${panelRecord ? " rc-panel-open" : ""}`}>
          <header className="rc-hdr">
            <div className="rc-hdr-in">
              <div className="rc-hdr-l">
                <span className="rc-title">Review Console</span>
                <span className="rc-badge">
                  {combinedAutoRecords.length +
                    reviewEphRecords.length +
                    reviewVendorRecords.length +
                    reviewNotFoundRecords.length}{" "}
                  records
                </span>
                <div className="rc-warn">
                  <IcoTriangle />
                  <span>
                    Review actions are available only until{" "}
                    <strong>{deadline}</strong>. After that, Review Required
                    records are read-only.
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Chip
                label={`Upload ID: ${sessionStorage.getItem("uploadId")}`}
                variant="outlined"
                sx={{
                  backgroundColor: "#f0f9ff",
                  color: "#0c4a6e",
                  fontWeight: 600,
                  border: "2px solid #0ea5e9",
                  fontSize: "0.90rem",
                  height: "auto",
                  padding: "4px 8px",
                  "& .MuiChip-label": {
                    paddingLeft: "6px",
                    paddingRight: "6px",
                  },
                }}
              />
              </div>
            </div>
          </header>

          <nav className="rc-tabbar">
            <div className="rc-tabbar-in">
              {saveToast.open && (
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 10,
                  }}
                >
                  <Alert severity={saveToast.severity} variant="filled">
                    {saveToast.message}
                  </Alert>
                </div>
              )}
              <div className="rc-tabs">
                {isExpeditedFlow && (
                  <button
                    className={`rc-tab${mainTab === "auto" ? " on" : ""}`}
                    onClick={() => setMainTab("auto")}
                  >
                    <IcoCheckCircle
                      cls={mainTab === "auto" ? "ic-c" : "ic-na"}
                    />
                    Auto Processed
                    <span
                      className={`rc-tab-bdg${mainTab === "auto" ? " active" : ""}`}
                      style={
                        mainTab !== "auto"
                          ? { background: "#f1f5f9", color: "#64748b" }
                          : {}
                      }
                    >
                      {combinedAutoRecords.length}
                    </span>
                  </button>
                )}
                <button
                  className={`rc-tab${mainTab === "review" ? " on" : ""}`}
                  onClick={() => setMainTab("review")}
                >
                  <IcoAlertCircle
                    cls={mainTab === "review" ? "ic-p" : "ic-na"}
                  />
                  Review Required
                  <span
                    className={`rc-tab-bdg${mainTab === "review" ? " active" : ""}`}
                    style={
                      mainTab !== "review"
                        ? { background: "#f1f5f9", color: "#64748b" }
                        : {}
                    }
                  >
                    {reviewEphRecords.length +
                      reviewVendorRecords.length +
                      reviewNotFoundRecords.length}
                  </span>
                </button>
              </div>
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                }}
              >
                {isExpeditedFlow &&
                  mainTab === "auto" &&
                  (autoFetching || autoPolling || manualRefreshing) && (
                  <span
                    className="rc-fetching-pill"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 14px",
                      borderRadius: 100,
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <CircularProgress
                      size={14}
                      thickness={5}
                      sx={{ color: "currentColor" }}
                    />
                    Loading result...
                  </span>
                )}
              </div>
              <div
                className="rc-tabbar-right"
                style={mainTab === "review" ? { paddingRight: 17 } : undefined}
              >
                <button
                  className="btn btn-o"
                  onClick={() => setShowUserGuide(true)}
                >
                  <HelpOutlineIcon sx={{ fontSize: 14 }} />
                  Help
                </button>
                {mainTab === "review" && !isExpeditedFlow && (
                  <button
                    className="btn btn-o"
                    disabled={
                      reviewLoading ||
                      allReviewRecords.length === 0 ||
                      isEveryReviewRecordSelected
                    }
                    onClick={() => setShowSkipReviewModal(true)}
                  >
                    Skip Review &amp; Select All
                  </button>
                )}
                {mainTab === "review" && (
                  <button
                    className="btn btn-r btn-no-hover"
                    disabled={!isSubmitReviewEnabled}
                    title={
                      !isReviewExpired && anyChecked && !allReviewTabsVisited
                        ? "Visit all tabs to enable Submit Review"
                        : undefined
                    }
                    onClick={() =>
                      isSubmitReviewEnabled && setShowSubmitModal(true)
                    }
                  >
                    <IcoSend cls={anyChecked ? "ic-white" : "ic-na"} />
                    Submit Review
                    {anyChecked && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "1px 6px",
                          borderRadius: 100,
                          background: "rgba(255,255,255,.2)",
                        }}
                      >
                        {totalChecked}
                      </span>
                    )}
                  </button>
                )}
                {isExpeditedFlow &&
                  mainTab === "auto" &&
                  autoPollExpired &&
                  !autoAllDone && (
                  <button
                    type="button"
                    onClick={handleManualAutoRefresh}
                    disabled={manualRefreshing}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#ffffff",
                      color: "#374151",
                      border: "1px solid #d1d5db",
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "8px 12px",
                      borderRadius: 10,
                      cursor: manualRefreshing ? "not-allowed" : "pointer",
                      opacity: manualRefreshing ? 0.65 : 1,
                    }}
                  >
                    {manualRefreshing ? (
                      <CircularProgress size={14} />
                    ) : (
                      <RefreshIcon sx={{ fontSize: 15 }} />
                    )}
                    Refresh
                  </button>
                )}
                <button
                  className="btn btn-r btn-no-hover"
                  onClick={
                    mainTab === "auto" ? handleExportAuto : handleExportReview
                  }
                  disabled={
                    mainTab === "auto"
                      ? !isExpeditedFlow ||
                        reviewLoading ||
                        !reviewLoaded ||
                        combinedAutoRecords.length === 0
                      : reviewLoading ||
                        (reviewSub === "eph"
                          ? reviewEphRecords.length === 0
                          : reviewSub === "vendor"
                            ? reviewVendorRecords.length === 0
                            : reviewNotFoundRecords.length === 0)
                  }
                >
                  <IcoDownload />
                  Export Results
                </button>
              </div>
            </div>
          </nav>

          <main className="rc-main">
            {isExpeditedFlow && mainTab === "auto" && (
              <div>
                <AutoProcessFilters
                  disabled={!hasAutoRecords}
                  filterOpen={filterOpen}
                  setFilterOpen={setFilterOpen}
                  hasFilters={hasFilters}
                  filterRS={filterRS}
                  setFilterRS={setFilterRS}
                  filterSource={filterSource}
                  setFilterSource={setFilterSource}
                  counts={counts}
                  subFilters={subFilters}
                  activeSubs={activeSubs}
                  addSubFilter={addSubFilter}
                  removeSubFilter={removeSubFilter}
                  updateSubFilter={updateSubFilter}
                  setAutoPage={setAutoPage}
                  onClearAll={() => {
                    setFilterRS(new Set());
                    setFilterSource(new Set());
                    setSubFilters([]);
                    setAutoPage(1);
                  }}
                />

                {reviewLoading && (
                  <Box style={{ marginTop: "10px" }}>
                    <LinearProgress
                      variant="indeterminate"
                      style={{
                        color: "rgb(185, 28, 28)",
                      }}
                      color="inherit"
                      sx={{ mb: 2, height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
                {reviewLoading && combinedAutoRecords.length === 0 ? (
                  <div className="rc-empty">
                    Loading Auto Processed records…
                  </div>
                ) : reviewError && combinedAutoRecords.length === 0 ? (
                  <div className="rc-empty">{reviewError}</div>
                ) : (
                  <RecordList
                    records={pagedAuto}
                    showStatus
                    onStatusClick={setModalRecord}
                    onRowClick={openPanel}
                    selections={selections}
                    isAutoProcessed
                    candidatesById={reviewCandidatesById}
                  />
                )}
                <Pagination
                  page={autoPage}
                  total={filteredAuto.length}
                  perPage={PAGE_SIZE}
                  onChange={(p) => {
                    setAutoPage(p);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </div>
            )}

            {mainTab === "review" &&
              (() => {
                const rvRecords = currentReviewSubRecords;
                const reviewed = reviewSubSearchRecords.filter((r) =>
                  checkedRecords.has(r.id),
                ).length;
                const pending = reviewSubSearchRecords.length - reviewed;
                const pagedRv = rvRecords.slice(
                  (reviewPage - 1) * PAGE_SIZE,
                  reviewPage * PAGE_SIZE,
                );
                return (
                  <div>
                    <div className="rc-rv-panel">
                      <div className="rc-rv-tabbar">
                        <div className="rc-rv-tabs">
                          {!isExpeditedFlow && (
                            <div className="rc-selall rc-selall-tab">
                              <button
                                className={`rc-selall-chk${
                                  isReviewHeaderCheckboxChecked
                                    ? " on"
                                    : isReviewHeaderCheckboxIndeterminate
                                      ? " ind"
                                      : ""
                                }`}
                                disabled={isReviewHeaderCheckboxDisabled}
                                onClick={() =>
                                  isReviewHeaderCheckboxChecked
                                    ? handleClearReviewSubSelection()
                                    : handleSelectAllRecords()
                                }
                                aria-label="Select all records"
                              >
                                {isReviewHeaderCheckboxChecked ? (
                                  <IcoSquareCheck />
                                ) : isReviewHeaderCheckboxIndeterminate ? (
                                  <IcoSquareMinus />
                                ) : (
                                  <IcoSquare />
                                )}
                              </button>
                              {!isExpeditedFlow && reviewSub !== "notfound" && (
                                <>
                                  <button
                                    type="button"
                                    aria-label="Select all options"
                                    disabled={isReviewHeaderCheckboxDisabled}
                                    onClick={(e) =>
                                      setSelectAllAnchor(e.currentTarget)
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      cursor: "pointer",
                                      display: "inline-flex",
                                    }}
                                  >
                                    <IcoChevDown size={12} cls="ic-na" />
                                  </button>
                                  <Menu
                                    anchorEl={selectAllAnchor}
                                    open={!!selectAllAnchor}
                                    onClose={() => setSelectAllAnchor(null)}
                                  >
                                    <MenuItem
                                      onClick={handleSelectAllAutoSelected}
                                    >
                                      Select all auto-selected results
                                    </MenuItem>
                                    <MenuItem
                                      onClick={() => {
                                        handleSelectAllRecords();
                                        setSelectAllAnchor(null);
                                      }}
                                    >
                                      Select all records
                                    </MenuItem>
                                  </Menu>
                                </>
                              )}
                            </div>
                          )}
                          {reviewSubTabs.map(({ k, lbl, cnt }) => (
                            <button
                              key={k}
                              className={`rc-rv-tab${reviewSub === k ? " on" : ""}`}
                              onClick={() => {
                                setReviewSub(k);
                                setVisitedReviewTabs((prev) =>
                                  new Set(prev).add(k),
                                );
                                setReviewPage(1);
                              }}
                            >
                              {lbl}
                              <span className="rc-rv-tab-bdg">{cnt}</span>
                            </button>
                          ))}
                        </div>
                        <div className="rc-rv-search-wrap">
                          <div className="rc-rv-search">
                            <IcoSearch size={13} cls="ic-na" />
                            <input
                              type="text"
                              placeholder="Search records..."
                              value={reviewSearchQuery}
                              onChange={(e) => {
                                setReviewSearchQuery(e.target.value);
                                setReviewPage(1);
                              }}
                            />
                          </div>
                        </div>
                        <div className="rc-rv-right">
                          <div className="rc-rv-stats">
                            <button
                              type="button"
                              className={`rc-rv-stat rc-rv-stat-filter all${
                                reviewStatusFilter === "all" ? " on" : ""
                              }`}
                              onClick={() => {
                                setReviewStatusFilter("all");
                                setReviewPage(1);
                              }}
                            >
                              <span className="rc-slbl">All</span>
                              <span className="rc-sval">
                                {reviewSubSearchRecords.length}
                              </span>
                            </button>
                            <button
                              type="button"
                              className={`rc-rv-stat rc-rv-stat-filter reviewed${
                                reviewStatusFilter === "reviewed" ? " on" : ""
                              }`}
                              onClick={() => {
                                setReviewStatusFilter("reviewed");
                                setReviewPage(1);
                              }}
                            >
                              <span className="rc-stat-dot rv review-dot-indicator" />
                              <span className="rc-slbl">Reviewed</span>
                              <span className="rc-sval">{reviewed}</span>
                            </button>
                            <button
                              type="button"
                              className={`rc-rv-stat rc-rv-stat-filter pending${
                                reviewStatusFilter === "pending" ? " on" : ""
                              }`}
                              onClick={() => {
                                setReviewStatusFilter("pending");
                                setReviewPage(1);
                              }}
                            >
                              <span className="rc-stat-dot pnd review-dot-indicator" />
                              <span className="rc-slbl">Pending</span>
                              <span className="rc-sval">{pending}</span>
                            </button>
                          </div>
                          <button
                            className="btn btn-o"
                            disabled={reviewLoading || isReviewExpired}
                            onClick={() => {
                              setClearEPH(true);
                              setClearVendor(true);
                              setClearNotFound(true);
                              setShowClearModal(true);
                            }}
                          >
                            <IcoX size={13} cls="ic-na" />
                            Clear Review
                          </button>
                          <button
                            className="btn btn-r btn-no-hover"
                            disabled={
                              isReviewExpired ||
                              reviewLoading ||
                              isSavingReview ||
                              !anyChecked
                            }
                            onClick={handleSaveReview}
                          >
                            <IcoCheckCircle2 size={13} />
                            Save My Review
                          </button>
                        </div>
                      </div>
                    </div>
                    {reviewLoading && (
                      <Box style={{ marginTop: "10px" }}>
                        <LinearProgress
                          variant="indeterminate"
                          style={{
                            color: "rgb(185, 28, 28)",
                          }}
                          color="inherit"
                          sx={{ mb: 2, height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                    {reviewLoading && rvRecords.length === 0 ? (
                      <div className="rc-empty">
                        Loading Review Required records…
                      </div>
                    ) : reviewError && rvRecords.length === 0 ? (
                      <div className="rc-empty">{reviewError}</div>
                    ) : (
                      <RecordList
                        records={pagedRv}
                        onRowClick={openPanel}
                        selections={selections}
                        showCheckbox
                        checkedRecords={checkedRecords}
                        onToggleCheck={toggleCheck}
                        candidatesById={reviewCandidatesById}
                        autoSelectEnabled={!isExpeditedFlow}
                      />
                    )}
                    <Pagination
                      page={reviewPage}
                      total={rvRecords.length}
                      perPage={PAGE_SIZE}
                      onChange={(p) => {
                        setReviewPage(p);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    />
                  </div>
                );
              })()}
          </main>

          {showClearModal && !isReviewExpired && (
            <ClearReviewModal
              onClose={() => setShowClearModal(false)}
              clearEPH={clearEPH}
              setClearEPH={setClearEPH}
              clearVendor={clearVendor}
              setClearVendor={setClearVendor}
              clearNotFound={clearNotFound}
              setClearNotFound={setClearNotFound}
              reviewEphRecords={reviewEphRecords}
              reviewVendorRecords={reviewVendorRecords}
              reviewNotFoundRecords={reviewNotFoundRecords}
              checkedRecords={checkedRecords}
              setCheckedRecords={setCheckedRecords}
              setSelections={setSelections}
            />
          )}

          {showSubmitModal && (
            <SubmitReviewModal
              onClose={() => setShowSubmitModal(false)}
              onConfirm={handleConfirmSubmitReview}
              reviewEphRecords={reviewEphRecords}
              reviewVendorRecords={reviewVendorRecords}
              reviewNotFoundRecords={reviewNotFoundRecords}
              checkedRecords={checkedRecords}
              ephAutoSelected={countCheckedAutoSelected(reviewEphRecords)}
              vendorAutoSelected={countCheckedAutoSelected(reviewVendorRecords)}
              showReviewed={isExpeditedFlow}
              isSubmittingReview={isSubmittingReview}
              submitReviewError={submitReviewError}
            />
          )}

          {showUserGuide && (
            <UserGuideModal
              isExpeditedFlow={isExpeditedFlow}
              onClose={() => setShowUserGuide(false)}
            />
          )}

          {showSkipReviewModal && (
            <SkipReviewModal
              onClose={() => setShowSkipReviewModal(false)}
              onConfirm={handleConfirmSkipReviewSelectAll}
            />
          )}

          {modalRecord && modalRecord.resultDetail && (
            <ResultModal
              record={modalRecord}
              onClose={() => setModalRecord(null)}
              deriveResultStatus={deriveResultStatus}
              rsMdlClass={rsClass}
            />
          )}
          {panelRecord && (
            <SidePanel
              record={panelRecord}
              candidates={getPanelData(
                panelRecord,
                mainTab === "auto",
                reviewCandidatesById,
              )}
              isAutoProcessed={mainTab === "auto"}
              selection={getSelection(panelRecord.id, mainTab === "auto")}
              onSelectionChange={(s) => setSelection(panelRecord.id, s)}
              onClose={closePanel}
              leftEntityType={panelRecord?.leftEntityType || "hcp"}
              rightEntityType={panelRecord?.rightEntityType || "hco"}
              vendorSearchState={reviewVendorSearchStatusById[panelRecord.id]}
              onVendorSearch={
                mainTab === "review" && !panelRecord.id.startsWith("VND-")
                  ? (entityType) =>
                      handleReviewVendorSearch(panelRecord.id, entityType)
                  : undefined
              }
            />
          )}
        </div>
      </Card>
    </div>
  );
}
